// @ts-nocheck
(() => {
  const root = document.querySelector('[data-odp-root]');
  if (!root) return;

  const PROXY = '/apps/orders';
  const CANCEL_REASONS = [
    'Ordered by mistake',
    'Changed my mind',
    'Found a better price',
    'No longer needed',
    'Shipping is taking too long',
    { value: 'Item ordered incorrectly (wrong size/color/variant)', label: 'Item ordered incorrectly', hint: '(wrong size/color/variant)' },
  ];
  // Pre-selected so the form is submittable without forcing a choice. Kept
  // neutral on purpose: defaulting to a fault reason ("Item ordered
  // incorrectly") would put words in the customer's mouth and skew the reason
  // data ops reads.
  const DEFAULT_CANCEL_REASON = 'No longer needed';
  // Returns have NO pre-selected reason: the form asks one question at a time,
  // and "What condition is it in?" only appears once a reason is chosen (owner,
  // 2026-10-08). A pre-filled reason would read as already answered.
  // Birch Credit is the default refund route (owner, confirmed 2026-10-07): it
  // lands faster for the customer and keeps the value with Birch. Original
  // payment method stays one click away. Registry-gift groups ignore both —
  // policy forces credit to the registrant regardless of what's selected here.
  const DEFAULT_REFUND_METHOD = 'store_credit';

  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('id');
  if (!orderId) {
    window.location.replace('/pages/orders');
    return;
  }

  init();

  async function init() {
    const { ok, status, data } = await call(`/orders/${orderId}`);
    const loadingEl = root.querySelector('[data-odp-loading]');
    const contentEl = root.querySelector('[data-odp-content]');

    if (status === 401 || status === 403) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.replace(`/customer_authentication/login?return_to=${returnUrl}`);
      return;
    }
    if (!ok || !data?.order) {
      if (loadingEl) loadingEl.innerHTML = '<p>Order not found. <a href="/pages/orders">Back to orders</a></p>';
      return;
    }

    const order = data.order;
    lineOptionsById = new Map(((order.lineItems && order.lineItems.nodes) || []).map(l => [l.id, lineOptionsText(l)]));
    // Return detail rides along with the order so each label renders beside the
    // items it covers on first paint.
    const returns = Array.isArray(data.returns) ? data.returns : [];
    // Per-line cancel/return windows from the vendor/product rule the order
    // was placed under (Birch Order Management → Shipping & Returns). Null when the
    // policy is off or the lookup failed — every window-aware branch below
    // then falls back to today's behaviour.
    policy = data.policy && data.policy.enabled ? data.policy : null;
    paymentSources = Array.isArray(data.paymentSources) ? data.paymentSources : [];
    keptCredits = Array.isArray(data.keptCredits) ? data.keptCredits : [];
    orderPayments = data.payments && Array.isArray(data.payments.paid) ? data.payments : null;
    if (loadingEl) loadingEl.hidden = true;
    contentEl.hidden = false;
    contentEl.innerHTML = renderPage(order, returns);

    upgradeDeliveryStatus(order);
    loadTracking();
    loadShipping(order);
    // The banners hide actions that no longer apply, so they must not run until
    // the per-shipment blocks exist — otherwise loadManage renders the buttons
    // back in after they were hidden.
    const managed = loadManage(order);
    loadCancellation(order, managed);
    loadReturn(order, managed);
    wireActions(order);
  }

  // ── Page render ──────────────────────────────────────────────────────────

  // This page reads the Admin GraphQL order via the App Proxy, where the enum is
  // `PARTIALLY_FULFILLED`. The markup was ported from page-orders.liquid, whose
  // storefront `order.fulfillment_status` says 'partial' — carrying that
  // vocabulary across meant every split-fulfillment order matched NOTHING here,
  // silently hiding the tracking section, the partial badge and the return
  // button. 'PARTIAL' stays accepted so the check can't break again if a caller
  // ever hands us the storefront value.
  const PARTIAL_STATUSES = ['PARTIALLY_FULFILLED', 'PARTIAL'];
  function isPartiallyFulfilled(o) {
    return PARTIAL_STATUSES.indexOf(String(o.displayFulfillmentStatus || '').toUpperCase()) !== -1;
  }

  // Items still waiting on a shipment, by remaining quantity — this is what makes
  // a split order legible ("1 of 3 shipped") rather than just "Shipped".
  function pendingItems(o) {
    return o.lineItems.nodes
      .filter(l => (l.unfulfilledQuantity || 0) > 0)
      .map(l => ({
        id: l.id,
        title: l.title,
        sku: l.sku || '',
        vendor: l.vendor || '',
        quantity: l.unfulfilledQuantity,
      }));
  }

  function renderPage(o, returns) {
    const isCancelled = !!o.cancelledAt;
    const isPaid = ['PAID', 'AUTHORIZED', 'PARTIALLY_PAID'].includes(o.displayFinancialStatus);
    const isFullyFulfilled = o.displayFulfillmentStatus === 'FULFILLED';
    const isPartial = isPartiallyFulfilled(o);
    // Any fulfillment at all means something shipped. Don't gate on the status
    // enum alone: IN_PROGRESS / ON_HOLD / SCHEDULED orders (common on mixed
    // Collective + first-party orders) can carry a shipped fulfillment too, and
    // those customers still need their tracking.
    const hasShipped = isFullyFulfilled || isPartial || o.fulfillments.length > 0;

    const pending = pendingItems(o);

    const iconType = isCancelled ? 'cancelled' : isFullyFulfilled ? 'done' : 'progress';

    return `
      <nav class="odp__breadcrumb">
        <a href="/pages/orders" class="odp__back">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>
          Your orders
        </a>
      </nav>

      <header class="odp__head">
        <div class="odp__head-id">
          <span class="odp__icon odp__icon--${iconType}" aria-hidden="true">${statusIcon(iconType)}</span>
          <div>
            <h1 class="odp__number">${escapeHtml(o.name)}</h1>
            <time class="odp__date" datetime="${escapeHtml(o.createdAt.slice(0, 10))}">
              Placed ${formatDate(o.createdAt, { month: 'long', day: 'numeric', year: 'numeric' })}
            </time>
          </div>
        </div>
        <div class="odp__badges">
          <span class="badge badge--${escapeHtml(o.displayFinancialStatus.toLowerCase())}">${escapeHtml(titleCase(o.displayFinancialStatus))}</span>
          ${isPartial ? '<span class="badge badge--partial">Partially shipped</span>' : ''}
          ${isFullyFulfilled ? '<span class="badge badge--fulfilled">Shipped</span>' : ''}
        </div>
      </header>

      ${isCancelled
        ? `<div class="odp__cancellation odp__cancellation--cancelled" data-cancellation>
             <p><strong>This order was cancelled on ${formatDate(o.cancelledAt, { month: 'long', day: 'numeric', year: 'numeric' })}.</strong>${o.cancelReason ? `<br><small>Reason: ${escapeHtml(titleCase(o.cancelReason))}</small>` : ''}</p>
           </div>`
        : `<div class="odp__cancellation" data-cancellation hidden></div>`
      }
      <div class="odp__return-status" data-return-status hidden></div>
      <div class="odp__delivery-alert" data-delivery-alert role="status" hidden></div>

      <div class="odp__body">
        <div class="odp__col-main">

          <div class="odp__section">
            <h2 class="odp__section-title">Order status</h2>
            ${renderProgress(o)}
          </div>

          <div class="odp__section" data-odp-shipments>
            <h2 class="odp__section-title">
              ${hasShipped || pendingShipments(o).length > 1 ? 'Shipments' : 'Items'}
              ${isPartial ? `<small class="odp__section-note">${escapeHtml(shippedSummary(o, pending))}</small>` : ''}
            </h2>
            <div class="odp__parcels">
              ${renderShipmentCards(o, returns)}
            </div>
          </div>

          ${renderInvoiceSection(o, isPaid)}

        </div>

        <div class="odp__col-aside">
          <div class="odp__aside-card">
            <h2 class="odp__aside-title">Payment summary</h2>
            ${renderPaymentSummary(o, isPaid)}
          </div>
          ${o.shippingAddress ? `
            <div class="odp__aside-card">
              <h2 class="odp__aside-title">Shipped to</h2>
              ${renderAddress(o.shippingAddress)}
            </div>` : ''}
          ${o.billingAddress ? `
            <div class="odp__aside-card">
              <h2 class="odp__aside-title">Billed to</h2>
              ${renderAddress(o.billingAddress)}
            </div>` : ''}
          ${renderCustomerNotes(o)}
        </div>
      </div>`;
  }

  // What the SHOPPER asked for at checkout: their gift message and/or their
  // special instructions, from the order note. The worker hands them over
  // already split out as `customerNotes` and never sends the raw note — that
  // also carries staff text and the internal "Gift Services:" lines meant for
  // the vendor packing the box (worker: src/order-notes/customer-notes.ts).
  //
  // Says it back, and nothing more: the note is frozen once the order is placed
  // and there is no way to change it, so this offers no correction route
  // (owner, 2026-10-10). Renders nothing when they added neither.
  function renderCustomerNotes(o) {
    const n = o.customerNotes;
    if (!n) return '';
    const rows = [];
    if (n.isGift) {
      rows.push(`
        <div class="odp__note">
          <p class="odp__note-label">${n.giftMessage ? 'Gift message' : 'Gift'}</p>
          ${n.giftMessage
            ? `<blockquote class="odp__note-quote">${nl2br(n.giftMessage)}</blockquote>`
            : `<p class="odp__note-text">You marked this order as a gift without adding a message.</p>`}
          ${renderGiftRecipient(n)}
        </div>`);
    }
    if (n.instructions) {
      rows.push(`
        <div class="odp__note">
          <p class="odp__note-label">Special instructions</p>
          <p class="odp__note-text">${nl2br(n.instructions)}</p>
        </div>`);
    }
    return `
      <div class="odp__aside-card">
        <h2 class="odp__aside-title">Your notes</h2>
        ${rows.join('')}
      </div>`;
  }

  // Who the gift notification goes to, as they typed it — the one piece of a
  // gift the customer cannot see anywhere else, and the one most worth a
  // second look before it sends to the wrong address.
  function renderGiftRecipient(n) {
    const r = n.giftRecipient;
    if (!r) return '';
    const how = r.channel === 'sms' ? 'texted to' : 'emailed to';
    const items = n.giftIncludeItems === null
      ? ''
      : ` ${n.giftIncludeItems ? "It lists what's inside." : "It doesn't list what's inside."}`;
    return `<p class="odp__note-meta">To be ${how} <strong>${escapeHtml(r.recipient)}</strong>.${items}</p>`;
  }

  // ── Shipment cards ───────────────────────────────────────────────────────
  //
  // One card per shipment, holding everything about it: its items, its tracking,
  // its return, its actions. The page previously listed the same items three
  // times — once under Tracking, once under Items, once under Manage order — and
  // the customer had to correlate them by eye. Amazon's order page is the model:
  // the shipment is the unit, and everything you can do to it lives on it.

  function buildShipments(o) {
    const byLineId = new Map(o.lineItems.nodes.map(l => [l.id, l]));
    return o.fulfillments.map((f) => {
      const items = ((f.fulfillmentLineItems && f.fulfillmentLineItems.nodes) || []).map(fli => {
        const id = (fli.lineItem && fli.lineItem.id) || '';
        return {
          lineItemId: id,
          line: byLineId.get(id) || null,
          title: (fli.lineItem && fli.lineItem.title) || '',
          sku: (fli.lineItem && fli.lineItem.sku) || '',
          vendor: (fli.lineItem && fli.lineItem.vendor) || '',
          quantity: fli.quantity || 0,
        };
      });
      const vendors = [...new Set(items.map(i => i.vendor).filter(Boolean))];
      const seen = new Set();
      const parcels = (f.trackingInfo || []).filter(t => {
        if (!t.number || seen.has(t.number)) return false;
        seen.add(t.number);
        return true;
      });
      return {
        id: f.id || '',
        items,
        vendor: vendors.length === 1 ? vendors[0] : '',
        parcels,
        shippedAt: f.createdAt || '',
        deliveredAt: f.deliveredAt || '',
        status: f.displayStatus || f.status || '',
        estimatedDelivery: f.estimatedDeliveryAt || '',
      };
    });
  }

  function renderShipmentCards(o, returns) {
    const shipments = buildShipments(o);
    const pending = pendingShipments(o);
    const showIndex = shipments.length + pending.length > 1;
    const claimed = new Set();
    keptCreditShown = new Set();
    const cards = shipments.map((s, i) => {
      const mine = returnsForShipment(returns, s);
      mine.forEach(r => claimed.add(r.id));
      return renderShipmentCard(s, i, showIndex, o, mine);
    });
    // A return whose items match no shipment (e.g. the fulfillment aged out of
    // the first-10 window) still needs its label shown somewhere.
    const orphans = (returns || []).filter(r => !claimed.has(r.id));
    if (orphans.length) cards.push(renderOrphanReturnCard(orphans));
    pending.forEach((group, i) => cards.push(renderPendingCard(o, group, {
      label: showIndex ? `Shipment ${shipments.length + i + 1}` : 'Not yet shipped',
      // The order-wide cancel (and any return with nothing shipped to sit on)
      // goes in ONE slot, on the last card still to ship.
      last: i === pending.length - 1,
    })));
    if (!cards.length) {
      // Nothing shipped and nothing outstanding — fall back to a plain list so
      // the page never renders an empty Items section.
      cards.push(`<div class="odp__parcel"><ul class="odp__items">${o.lineItems.nodes.map(l => renderLineItem(l, o.currencyCode, false)).join('')}</ul></div>`);
    }
    return cards.join('');
  }

  function returnsForShipment(returns, shipment) {
    const ids = new Set(shipment.items.map(i => i.lineItemId).filter(Boolean));
    return (returns || []).filter(r => (r.items || []).some(it => ids.has(it.lineItemId)));
  }

  function renderShipmentCard(s, index, showIndex, o, returns) {
    const state = fulfillmentState(s);
    const when = s.deliveredAt || s.shippedAt;
    const dateLabel = s.deliveredAt ? 'Delivered' : 'Shipped';
    return `
      <div class="odp__parcel" data-shipment="${escapeHtml(s.id)}"${s.deliveredAt ? ' data-delivered' : ''}>
        <div class="odp__parcel-head">
          <div>
            ${showIndex ? `<p class="odp__parcel-label">Shipment ${index + 1}</p>` : ''}
            ${s.vendor ? `<p class="odp__parcel-vendor">${escapeHtml(s.vendor)}</p>` : ''}
            <p class="odp__parcel-estimate" data-parcel-estimate hidden></p>
          </div>
          <div class="odp__parcel-state">
            ${state ? `<span class="tracking-block__state tracking-block__state--${state.tone}" data-shipment-state>${escapeHtml(state.label)}</span>` : ''}
            ${when ? `<time class="odp__parcel-date">${dateLabel} ${formatDate(when, { month: 'short', day: 'numeric' })}</time>` : ''}
          </div>
        </div>

        <ul class="odp__items">
          ${s.items.map(it => renderShipmentItem(it, o.currencyCode)).join('')}
        </ul>

        ${s.parcels.length ? `
          <div class="odp__parcel-tracking">
            ${s.parcels.map(t => `
              <p class="odp__parcel-carrier">
                <span>${escapeHtml(t.company || 'Carrier')} <strong>${escapeHtml(t.number)}</strong></span>
                ${t.url ? `<a class="tracking-link" href="${escapeHtml(t.url)}" target="_blank" rel="noopener noreferrer">Track ${escapeHtml(String.fromCharCode(8599))}</a>` : ''}
              </p>`).join('')}
            <div data-shipment-detail></div>
          </div>` : `<div class="odp__parcel-tracking"><p class="odp__parcel-carrier"><small>No tracking number provided for this shipment.</small></p><div data-shipment-detail></div></div>`}

        ${returns.map(r => renderReturnCard(r, s)).join('')}
        ${renderKeptCredits(s)}

        <div class="odp__parcel-actions" data-shipment-actions="${escapeHtml(s.id)}"></div>
        <div class="odp__action-panel" data-shipment-panel="${escapeHtml(s.id)}" hidden></div>
      </div>`;
  }

  // What the line was bought as — "Print: Blush · Size: Preemie-NB". Built by
  // the app (birch-order-management src/combined-selection/): a combined-listing
  // child's own title and "Default Title" variant never say which colourway it
  // is, so the parent's option value comes first, then the variant's options.
  function lineOptionsText(line) {
    const opts = (line && Array.isArray(line.options)) ? line.options : [];
    return opts
      .filter(o => o && o.name && o.value)
      .map(o => `${o.name}: ${o.value}`)
      .join(' · ');
  }

  // Return items arrive with only a lineItemId and a title; two colourways of
  // one family share the title, so the return form names the options too.
  let lineOptionsById = new Map();
  function optionsSuffix(lineItemId) {
    const text = lineOptionsById.get(lineItemId);
    return text ? ` <small class="odp__item-options">${escapeHtml(text)}</small>` : '';
  }

  function optionsLine(line) {
    const text = lineOptionsText(line);
    return text ? `<p class="odp__item-variant">${escapeHtml(text)}</p>` : '';
  }

  // Compact item row: image, title, per-line shipped state comes from the card
  // it sits in, so no per-row badge is needed any more.
  function renderShipmentItem(it, currencyCode) {
    const line = it.line;
    const url = productUrl(line);
    const img = line && line.image
      ? `<img src="${escapeHtml(line.image.url)}" alt="${escapeHtml(line.image.altText || it.title)}" loading="lazy" class="odp__item-img" />`
      : '<div class="odp__item-img odp__item-img--placeholder" aria-hidden="true"></div>';
    const price = line ? `<p class="odp__item-total">${formatMoney(lineTotal(line), currencyCode)}</p>` : '';
    const lineItemId = it.lineItemId || it.id || '';
    return `
      <li class="odp__item" data-line-item-id="${escapeHtml(lineItemId)}">
        ${linkTo(url, img, 'odp__item-img-link')}
        <div class="odp__item-text">
          <p class="odp__item-title">${linkTo(url, escapeHtml(it.title), 'odp__item-link')}</p>
          ${optionsLine(line)}
          ${it.sku ? `<p class="odp__item-variant">SKU: ${escapeHtml(it.sku)}</p>` : ''}
          ${shippingPolicyLine(lineItemId)}
          ${policyTermsLine(lineItemId)}
          <p class="odp__item-meta"><span>Qty: ${it.quantity}</span></p>
        </div>
        ${price}
      </li>`;
  }

  // Items still to ship, one group per shipment Shopify planned for them (its
  // open fulfillment orders, from GET /orders/:id), so a split order shows its
  // shipments separately from the start rather than only once one has gone.
  // Whatever no fulfillment order accounts for (none read, or quantities that
  // disagree) gets a group of its own, so no item ever drops off the page.
  function pendingShipments(o) {
    const pending = pendingItems(o);
    if (!pending.length) return [];
    const left = new Map(pending.map(p => [p.id, p.quantity]));
    const byId = new Map(pending.map(p => [p.id, p]));
    const groups = [];
    ((o.fulfillmentOrders && o.fulfillmentOrders.nodes) || []).forEach((fo) => {
      const items = [];
      ((fo && fo.lineItems && fo.lineItems.nodes) || []).forEach((n) => {
        const id = n && n.lineItem && n.lineItem.id;
        const quantity = Math.min(n.remainingQuantity || 0, left.get(id) || 0);
        if (!id || quantity <= 0) return;
        left.set(id, left.get(id) - quantity);
        items.push({ ...byId.get(id), quantity });
      });
      if (items.length) groups.push({ id: fo.id, items });
    });
    const rest = pending.filter(p => left.get(p.id) > 0).map(p => ({ ...p, quantity: left.get(p.id) }));
    if (rest.length) groups.push({ id: 'pending-rest', items: rest });
    return groups;
  }

  function renderPendingCard(o, group, { label, last }) {
    const byId = new Map(o.lineItems.nodes.map(l => [l.id, l]));
    const items = group.items.map(p => ({ ...p, line: byId.get(p.id) || null }));
    return `
      <div class="odp__parcel odp__parcel--pending" data-shipment="${last ? 'pending' : escapeHtml(group.id)}">
        <div class="odp__parcel-head">
          <div>
            <p class="odp__parcel-label">${escapeHtml(label)}</p>
            <p class="odp__parcel-estimate" data-parcel-estimate hidden></p>
          </div>
          <div class="odp__parcel-state">
            <span class="tracking-block__state tracking-block__state--pending">Preparing</span>
          </div>
        </div>
        <ul class="odp__items">
          ${items.map(it => renderShipmentItem(it, o.currencyCode)).join('')}
        </ul>
        <p class="odp__parcel-carrier"><small>We'll email you tracking as soon as these are on their way.</small></p>
        ${last ? `<div class="odp__parcel-actions" data-shipment-actions="pending"></div>
        <div class="odp__action-panel" data-shipment-panel="pending" hidden></div>` : ''}
      </div>`;
  }

  function renderOrphanReturnCard(returns) {
    return `
      <div class="odp__parcel odp__parcel--return-only">
        <div class="odp__parcel-head">
          <div><p class="odp__parcel-label">Return</p></div>
        </div>
        ${returns.map(r => renderReturnCard(r, null)).join('')}
      </div>`;
  }

  function fulfillmentState(s) {
    if (s.deliveredAt) return SHIPMENT_STATES.DELIVERED;
    const raw = String(s.status || '').toUpperCase().replace(/[\s-]+/g, '_');
    if (!raw) return null;
    return SHIPMENT_STATES[raw] || { label: titleCase(raw), tone: 'transit' };
  }

  // Shopify Return.status. OPEN means the return is authorised and in flight —
  // "in progress" reads better to a customer than the enum.
  const RETURN_STATES = {
    OPEN: { label: 'Return in progress', tone: 'transit' },
    REQUESTED: { label: 'Return requested', tone: 'pending' },
    DECLINED: { label: 'Return declined', tone: 'alert' },
    CANCELED: { label: 'Return cancelled', tone: 'alert' },
    CANCELLED: { label: 'Return cancelled', tone: 'alert' },
    CLOSED: { label: 'Return complete', tone: 'done' },
  };

  // The return label, sitting with the items it covers. `shipment` scopes the
  // item list to that card; pass null to show every item on the return.
  function renderReturnCard(r, shipment) {
    const state = RETURN_STATES[String(r.status || '').toUpperCase()]
      || { label: 'Return in progress', tone: 'transit' };
    const ids = shipment ? new Set(shipment.items.map(i => i.lineItemId)) : null;
    const items = (r.items || []).filter(it => !ids || ids.has(it.lineItemId));
    // Shopify hands back an all-zero placeholder before the carrier scans the
    // label; showing "FedEx 0000000000" is worse than showing nothing.
    const hasTracking = r.trackingNumber && /[1-9]/.test(r.trackingNumber);
    const labelId = `label-${String(r.id).split('/').pop()}`;
    return `
      <div class="odp__return" data-return-id="${escapeHtml(r.id)}">
        <div class="odp__return-head">
          <span class="odp__return-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 14 4 9 9 4"></polyline><path d="M20 20v-7a4 4 0 0 0-4-4H4"></path></svg>
          </span>
          <span class="tracking-block__state tracking-block__state--${state.tone}">${escapeHtml(state.label)}</span>
        </div>
        ${items.length ? `<ul class="odp__return-items">${items.map(it => `
          <li>${escapeHtml(it.title)}${optionsSuffix(it.lineItemId)}${it.quantity > 1 ? ` <small>&times; ${it.quantity}</small>` : ''}${it.returnReason ? ` <small class="odp__return-reason">${escapeHtml(titleCase(it.returnReason))}</small>` : ''}${itemAnswerLine(r, it.lineItemId)}</li>`).join('')}</ul>` : ''}
        ${r.condition && !hasItemAnswers(r) ? `<p class="odp__return-refund"><small>Condition: ${escapeHtml(r.condition)}</small></p>` : ''}
        ${refundLine(r.refund)}
        ${r.labelUrl ? `
          <div class="odp__label">
            <div class="odp__label-row">
              <span class="odp__label-name">Return shipping label${hasTracking ? ` &middot; ${escapeHtml(r.trackingCarrier || 'Carrier')} ${escapeHtml(r.trackingNumber)}` : ''}</span>
              <span class="odp__label-actions">
                <button type="button" class="btn-outline btn-sm" data-odp-action="label-preview" data-label-target="${labelId}" aria-expanded="false" aria-controls="${labelId}">Preview</button>
                <a class="btn-outline btn-sm" href="${escapeHtml(r.labelUrl)}" target="_blank" rel="noopener noreferrer" download>Download</a>
              </span>
            </div>
            <div class="odp__label-preview" id="${labelId}" data-label-src="${escapeHtml(r.labelUrl)}" hidden></div>
          </div>` : `<p class="odp__return-pending"><small>Your return label will appear here once it's issued — we'll email you too.</small></p>`}
        ${hasTracking && r.trackingUrl ? `<p class="odp__return-track"><a class="tracking-link" href="${escapeHtml(r.trackingUrl)}" target="_blank" rel="noopener noreferrer">Track this return ${escapeHtml(String.fromCharCode(8599))}</a></p>` : ''}
      </div>`;
  }

  // One returned item's own answers (returns filed with per-item answers):
  // "Opened, but never used · Exchange for Medium".
  function hasItemAnswers(r) {
    return !!(r.answers && Object.keys(r.answers).length);
  }
  function itemAnswerLine(r, lineItemId) {
    const a = r.answers && r.answers[lineItemId];
    if (!a) return '';
    const parts = [];
    if (a.condition) parts.push(a.condition);
    if (a.resolution === 'exchange') parts.push(a.exchangeTitle ? `Exchange for ${a.exchangeTitle}` : 'Exchange');
    else if (r.refund && r.refund.method === 'exchange') parts.push('Exchange');
    else if (Object.values(r.answers).some(x => x.resolution === 'exchange')) parts.push('Refund');
    return parts.length ? `<br><small class="odp__return-reason">${escapeHtml(parts.join(' · '))}</small>` : '';
  }

  // How this return's money comes back, as the customer chose it (worded by
  // the worker; absent for returns made outside the order page / Birch AI).
  function refundLine(refund) {
    if (!refund || !refund.label) return '';
    const lead = refund.method === 'exchange' ? '' : refund.refunded ? 'Refunded to ' : 'Refund to ';
    return `<p class="odp__return-refund"><small>${escapeHtml(lead + refund.label)}</small></p>`;
  }

  // Kept with a keep-it credit — sits on the shipment that delivered the items.
  // A line split across parcels is on more than one card; its credit was issued
  // once, so it is shown once, on the first card that holds the line.
  function renderKeptCredits(s) {
    const ids = new Set(s.items.map(i => i.lineItemId).filter(Boolean));
    const mine = keptCredits.filter(k => ids.has(k.lineItemId) && !keptCreditShown.has(k.lineItemId));
    if (!mine.length) return '';
    mine.forEach(k => keptCreditShown.add(k.lineItemId));
    return mine.map(k => {
      const line = (s.items.find(i => i.lineItemId === k.lineItemId) || {});
      return `<p class="odp__return-refund odp__return-refund--kept"><small>Kept ${escapeHtml(line.title || (line.line && line.line.title) || 'item')}${k.quantity > 1 ? ` &times; ${k.quantity}` : ''} — ${escapeHtml(formatMoney(k.amount, k.currency))} added to your Birch Credit</small></p>`;
    }).join('');
  }

  // "1 of 3 items shipped" — plain-language framing of a split fulfillment.
  function shippedSummary(o, pending) {
    const total = o.lineItems.nodes.reduce((n, l) => n + (l.quantity || 0), 0);
    const waiting = pending.reduce((n, i) => n + (i.quantity || 0), 0);
    return `${total - waiting} of ${total} item${total === 1 ? '' : 's'} shipped`;
  }

  function renderProgress(o) {
    const isCancelled = !!o.cancelledAt;
    const isPaid = ['PAID', 'AUTHORIZED', 'PARTIALLY_PAID'].includes(o.displayFinancialStatus);
    const isFullyFulfilled = o.displayFulfillmentStatus === 'FULFILLED';
    const pending = pendingItems(o);
    // Treat "has fulfillments but items still outstanding" as partial even when
    // Shopify's order-level status hasn't caught up (or reports something else
    // entirely, e.g. IN_PROGRESS on a mixed Collective order). Marking such an
    // order as fully Shipped is exactly the bug this page had.
    const isPartial = isPartiallyFulfilled(o) || (o.fulfillments.length > 0 && pending.length > 0);
    const hasShipped = isFullyFulfilled || isPartial || o.fulfillments.length > 0;

    // First fulfillment date for the shipped step timestamp
    const firstFulfillment = o.fulfillments[0];
    const hasTracking = o.fulfillments.some(f => f.trackingInfo.some(t => t.number));

    if (isCancelled) {
      return `
        <ol class="odp-progress odp-progress--cancelled" data-odp-progress>
          <li class="odp-progress__step is-complete">
            <span class="odp-progress__icon" aria-hidden="true">${checkIcon(18)}</span>
            <span class="odp-progress__label">Order placed</span>
            <time class="odp-progress__time">${formatDate(o.createdAt, { month: 'short', day: 'numeric' })}</time>
          </li>
          <li class="odp-progress__step is-cancelled">
            <span class="odp-progress__icon" aria-hidden="true">${cancelIcon(18)}</span>
            <span class="odp-progress__label">Cancelled</span>
            <time class="odp-progress__time">${formatDate(o.cancelledAt, { month: 'short', day: 'numeric' })}</time>
          </li>
        </ol>`;
    }

    return `
      <ol class="odp-progress" data-odp-progress>
        <li class="odp-progress__step is-complete">
          <span class="odp-progress__icon" aria-hidden="true">${checkIcon(18)}</span>
          <span class="odp-progress__label">Authorized</span>
          <time class="odp-progress__time">${formatDate(o.createdAt, { month: 'short', day: 'numeric' })}</time>
        </li>
        <li class="odp-progress__step${isPaid ? ' is-complete' : ''}">
          <span class="odp-progress__icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg></span>
          <span class="odp-progress__label">Processing</span>
        </li>
        <li class="odp-progress__step${isPartial ? ' is-partial' : hasShipped ? ' is-complete' : ''}" data-progress-step="shipped">
          <span class="odp-progress__icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13" rx="1"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg></span>
          <span class="odp-progress__label">${isPartial ? 'Partially shipped' : 'Shipped'}</span>
          ${firstFulfillment ? `<time class="odp-progress__time">${formatDate(firstFulfillment.createdAt, { month: 'short', day: 'numeric' })}</time>` : ''}
          ${isPartial ? `<small class="odp-progress__hint">${escapeHtml(shippedSummary(o, pending))}</small>` : ''}
          ${hasShipped && !hasTracking ? '<small class="odp-progress__hint">Tracking not provided</small>' : ''}
        </li>
        <li class="odp-progress__step" data-progress-step="delivered">
          <span class="odp-progress__icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12L12 3l9 9"></path><path d="M5 10v10h14V10"></path></svg></span>
          <span class="odp-progress__label">Delivered</span>
        </li>
      </ol>`;
  }

  function renderLineItem(line, currencyCode, hasShipped) {
    // Per-line shipped state — only meaningful once something has shipped, and
    // the clearest answer to "did MY item go out?" on a split order.
    const waiting = line.unfulfilledQuantity || 0;
    const shipState = !hasShipped ? null
      : waiting === 0 ? { label: 'Shipped', mod: 'shipped' }
      : waiting < line.quantity ? { label: `${line.quantity - waiting} of ${line.quantity} shipped`, mod: 'partial' }
      : { label: 'Not yet shipped', mod: 'pending' };
    const url = productUrl(line);
    const img = line.image
      ? `<img src="${escapeHtml(line.image.url)}" alt="${escapeHtml(line.image.altText || line.title)}" loading="lazy" class="odp__item-img" />`
      : `<div class="odp__item-img odp__item-img--placeholder" aria-hidden="true"></div>`;
    return `
      <li class="odp__item" data-line-item-id="${escapeHtml(line.id || '')}">
        ${linkTo(url, img, 'odp__item-img-link')}
        <div class="odp__item-text">
          <p class="odp__item-title">${linkTo(url, escapeHtml(line.title), 'odp__item-link')}</p>
          ${shipState ? `<p class="odp__item-ship odp__item-ship--${shipState.mod}">${escapeHtml(shipState.label)}</p>` : ''}
          ${optionsLine(line)}
          ${line.sku ? `<p class="odp__item-variant">SKU: ${escapeHtml(line.sku)}</p>` : ''}
          ${line.vendor ? `<p class="odp__item-vendor">Supplied by ${escapeHtml(line.vendor)}</p>` : ''}
          ${shippingPolicyLine(line.id)}
          ${policyTermsLine(line.id)}
          <p class="odp__item-meta">
            <span>Qty: ${line.quantity}</span>
            <span aria-hidden="true">&middot;</span>
            <span>${formatMoney(line.originalUnitPriceSet.shopMoney.amount, currencyCode)} each</span>
          </p>
        </div>
        <p class="odp__item-total">${formatMoney(lineTotal(line), currencyCode)}</p>
      </li>`;
  }

  // Returnable groups for this order, cached from loadManage so a click doesn't
  // re-fetch. Shopify groups by return DESTINATION, so two parcels going back to
  // the same place are deliberately one group.
  let returnableGroups = [];

  // The return form's questions, from /returnable `flow` — set in Birch Order
  // Management → Shipping & Returns → Return questions & exchanges, the same
  // settings Birch AI asks from. Shape:
  //   { askCondition, conditions: [{ id, label }], exchanges }
  // Null (older worker, or the policy couldn't be read) = ask nothing extra
  // and offer a refund only, as before.
  let returnFlow = null;

  // The order's return-policy view (see the detail fetch). Shape:
  //   { enforce, cancel: { eligible, deadlineAt, reason, lines[] },
  //     returns: { byLineItem: { [lineItemId]: { eligible, deadlineAt, reason, label, terms } } } }
  // `terms` is { returns, cancellation }: the line's terms where they differ
  // from the store standard, already worded; "" = standard, show nothing.
  let policy = null;

  // What the order was paid with, worded by the worker ("Shop Pay (Visa ending
  // 4242)", "PayPal", "Apple Pay (Amex ending 1001)"; several for a split
  // payment) — where an original-payment refund goes. [] = unknown (older
  // worker, manual payment): say "original payment method".
  let paymentSources = [];
  // Items kept with a keep-it credit: [{ lineItemId, quantity, amount, currency }].
  let keptCredits = [];
  // Lines whose credit is already on a card in the current render.
  let keptCreditShown = new Set();
  // The invoice's payment rows, Shopify's own figures (decimal strings):
  //   { paid: [{ label, amount }], refunds: [{ at, amount, to: [{ label, amount }] }],
  //     totalRefunded, netPaid }
  // Null with an older worker: the invoice then shows no payment section.
  let orderPayments = null;
  function originalPaymentLabel() {
    return paymentSources.length ? paymentSources.join(' + ') : '';
  }

  // The item's non-standard return/cancellation terms, as sold — shown
  // whether or not the item has shipped. Both item renderers call it: the
  // shipment cards (renderShipmentItem) and the plain list (renderLineItem).
  function policyTermsLine(lineItemId) {
    const w = policy && policy.returns && policy.returns.byLineItem && policy.returns.byLineItem[lineItemId];
    const t = w && w.terms;
    if (!t) return '';
    return (t.returns ? `<p class="odp__item-policy-terms">Returns: ${escapeHtml(t.returns)}</p>` : '')
      + (t.cancellation ? `<p class="odp__item-policy-terms">${escapeHtml(t.cancellation)}</p>` : '');
  }

  // The order's shipping record (Birch Order Management → Shipping & Returns),
  // frozen when it was placed — see loadShipping. Shape:
  //   { lines: { [lineItemId]: { text, source, method, shipBy } },
  //     promise: { methods, shippingLines, deliveries, … } }
  // `text` is the shipping policy the item was sold under; null when none
  // applied (the theme's default claim) or the order predates the record, and
  // then the item shows nothing extra — exactly as before.
  let shipping = null;

  // That record set against the order as it stands (served beside it):
  //   { estimate: [{ from, to, method, lineItemIds }],   checkout's delivery dates
  //     lines: { [lineItemId]: { kind: 'ship'|'delivery', due, open, late } },
  //     late, lateKind: 'ship'|'delivery'|'both'|null, since, settled }
  // Dates are calendar days, "YYYY-MM-DD". Null = nothing promised or not
  // loaded; the page then shows no estimate and no alert.
  let delivery = null;

  // A cancelled order shows no delivery estimates.
  let orderCancelled = false;

  function shippingPolicyLine(lineItemId) {
    const l = shipping && shipping.lines && shipping.lines[lineItemId];
    const text = l && l.text ? `<p class="odp__item-shipping-policy" data-item-shipping>${escapeHtml(l.text)}</p>` : '';
    // A ship-by date shows while the item waits to ship; once shipped, the
    // shipment card says the rest.
    const d = delivery && delivery.lines && delivery.lines[lineItemId];
    const shipBy = d && d.kind === 'ship' && d.open
      ? (d.late
        ? `<p class="odp__item-ship-by is-late" data-item-shipping>Delayed — was expected to ship by ${escapeHtml(formatDay(d.due))}</p>`
        : `<p class="odp__item-ship-by" data-item-shipping>Expected to ship by ${escapeHtml(formatDay(d.due))}</p>`)
      : '';
    return text + shipBy;
  }

  // Fetched only once the order is on screen, never before it: an order with
  // no stored record is read live from Shopify, and the page must not wait on
  // that. Each item's lines slot in where the render would have put them, the
  // estimate under the progress bar and any alert under the page head; a
  // failed or empty answer leaves the page exactly as it is.
  async function loadShipping(order) {
    const { ok, data } = await call(`/orders/${orderId}/shipping`).catch(() => ({ ok: false, data: null }));
    if (!ok || !data || !data.shipping) return;
    shipping = data.shipping;
    delivery = data.delivery || null;
    orderCancelled = !!(order && order.cancelledAt);
    root.querySelectorAll('.odp__item[data-line-item-id]').forEach((item) => {
      if (item.querySelector('[data-item-shipping]')) return;
      const line = shippingPolicyLine(item.getAttribute('data-line-item-id'));
      const meta = item.querySelector('.odp__item-meta');
      if (line && meta) meta.insertAdjacentHTML('beforebegin', line);
    });
    renderShippingMethod();
    renderEstimates();
    renderDelivery();
  }

  // The rate(s) the customer chose at checkout, beside the Shipping total —
  // the email's "Shipping Method". From the record's shippingLines, one per
  // rate (a split order can carry two); none recorded leaves the row as is.
  function renderShippingMethod() {
    const lines = (shipping.promise && shipping.promise.shippingLines) || [];
    const titles = [...new Set(lines.map(l => l && l.title && String(l.title).trim()).filter(Boolean))];
    if (!titles.length) return;
    root.querySelectorAll('[data-shipping-method]').forEach((el) => {
      el.textContent = titles.join(' + ');
      el.hidden = false;
    });
  }

  // The running-late alert while anything is past its date.
  function renderDelivery() {
    const alert = root.querySelector('[data-delivery-alert]');
    if (!delivery) return;
    if (alert && delivery.late) {
      const lines = Object.values(delivery.lines || {});
      const earliest = (ls) => ls.map(l => l.due).sort()[0];
      const deliveries = lines.filter(l => l.kind === 'delivery');
      const lateDeliveries = deliveries.filter(l => l.late);
      const lateShips = lines.filter(l => l.kind === 'ship' && l.late);
      const parts = [];
      if (lateDeliveries.length) {
        parts.push(`We estimated this order would arrive by ${formatDay(earliest(lateDeliveries))}, and ${lateDeliveries.length === deliveries.length ? "it hasn't been delivered yet" : "some items haven't arrived yet"}.`);
      }
      if (lateShips.length) {
        parts.push(lateShips.length === 1
          ? `An item was expected to ship by ${formatDay(lateShips[0].due)} and hasn't shipped yet.`
          : `${lateShips.length} items were expected to ship by ${formatDay(earliest(lateShips))} and haven't shipped yet.`);
      }
      if (!parts.length) return;
      alert.innerHTML = `<p><strong>Running late.</strong> ${escapeHtml(parts.join(' '))}</p>`;
      alert.hidden = false;
    }
  }

  // Each shipment's estimate, in its own card beside its items: checkout's
  // delivery dates ("Estimated delivery Tue, Sep 29 – Thu, Oct 1") while any
  // of its items is still on the way, else the words checkout showed under its
  // rate ("Standard · Delivery: 2 to 8 business days after shipping", or a
  // transit time, "Standard · 3 to 5 business days") from the record's
  // promise.deliveries. Matched by line item, so it follows the items into
  // whichever card holds them, shipped or not. None on a delivered card or a
  // cancelled order. A version-3 record's one string names no items, so it
  // shows only when the order has a single card.
  function renderEstimates() {
    if (orderCancelled) return;
    const promise = (shipping && shipping.promise) || {};
    const rows = Array.isArray(promise.deliveries) ? promise.deliveries.filter(r => r && r.wording) : [];
    const windows = ((delivery && delivery.estimate) || []).filter(w => w && w.to);
    const onTheWay = (id) => {
      const l = delivery && delivery.lines && delivery.lines[id];
      return !l || l.open;
    };
    const cards = [...root.querySelectorAll('.odp__parcel[data-shipment]')];
    cards.forEach((card) => {
      const slot = card.querySelector('[data-parcel-estimate]');
      if (!slot || card.hasAttribute('data-delivered')) return;
      const ids = new Set([...card.querySelectorAll('[data-line-item-id]')].map(el => el.getAttribute('data-line-item-id')));
      const mine = (list) => (list || []).filter(id => ids.has(id));
      const dated = windows.filter(w => mine(w.lineItemIds).some(onTheWay));
      let html = '';
      if (dated.length) {
        html = `Estimated delivery ${[...new Set(dated.map(dayRange))].map(r => `<strong>${escapeHtml(r)}</strong>`).join(', ')}`;
      } else {
        const words = [...new Set(rows.filter(r => mine(r.lineItemIds).length).map(r => (r.rate ? `${r.rate} · ${r.wording}` : r.wording)))];
        if (!words.length && !rows.length && cards.length === 1 && typeof promise.deliveryDescription === 'string') {
          words.push(...promise.deliveryDescription.split('\n').map(w => w.trim()).filter(Boolean));
        }
        html = words.map(escapeHtml).join('<br>');
      }
      if (!html) return;
      slot.innerHTML = html;
      slot.hidden = false;
    });
  }

  // A calendar day ("2026-09-29") as "Tue, Sep 29". Read as UTC noon so no
  // browser time zone can move it to a neighbouring day.
  function formatDay(day) {
    return formatDate(`${day}T12:00:00Z`, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  function dayRange(w) {
    return w.from && w.from !== w.to ? `${formatDay(w.from)} – ${formatDay(w.to)}` : formatDay(w.to);
  }

  // "Sep 14, 3:15 PM" for a deadline within the next day, "Sep 14" otherwise —
  // an hour-scale cancellation window needs the time, a return window doesn't.
  function formatDeadline(ms, withTime) {
    return formatDate(new Date(ms).toISOString(), withTime
      ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // Drops each action onto the card it belongs to: "Request return" on the
  // shipment holding those items, "Cancel" on the not-yet-shipped card.
  // Shipments a return was started on during this visit: their button reads
  // "Return other items" (the new return's card only appears on reload).
  const submittedShipments = new Set();

  function shipmentIdsFor(o, g) {
    const ids = new Set(g.items.map(it => it.lineItemId));
    return buildShipments(o).filter(s => s.items.some(it => ids.has(it.lineItemId))).map(s => s.id);
  }

  // (Re)places every "Request return" action from a fresh /returnable — on
  // load, and again right after a submit so items still returnable (others in
  // the shipment, the rest of a quantity) keep a way back in. Returns
  // 'group_gift_contribution' when the order has nothing to return by policy.
  async function refreshReturnActions(o) {
    const { ok, data } = await call(`/orders/${orderId}/returnable`).catch(() => ({ ok: false, data: null }));
    // A failed lookup used to wipe every button and redraw from an empty list,
    // so one blip right after a submit left no way to return anything else
    // until a reload. Keep what is on the page and the cached groups instead.
    if (!ok || !data) return 'unavailable';
    root.querySelectorAll('[data-return-action], [data-return-closed]').forEach(el => el.remove());
    if (data.notReturnableReason === 'group_gift_contribution') {
      const first = root.querySelector('[data-shipment-actions]');
      if (first) first.innerHTML = renderGiftContributionNotice(data);
      return 'group_gift_contribution';
    }
    returnableGroups = Array.isArray(data.groups) ? data.groups : [];
    if (data.flow) returnFlow = data.flow;

    const shipments = buildShipments(o);
    returnableGroups.forEach((g, gi) => {
      // A group is one return DESTINATION, and the server now keys it by
      // location AND vendor, so a group spanning parcels means a genuine
      // same-supplier split — not two unrelated boxes collapsed together.
      //
      // Put the button on EVERY shipment the group touches rather than
      // anchoring it on the first. The old anchor meant a customer looking at
      // the parcel they actually wanted to send back saw no way to return it,
      // while an unrelated parcel above carried a button that silently swept it
      // in. All the copies share one group index and open the same form.
      // Every item in the group outside its window (enforced) → no button; the
      // items' own windows are explained on the card by the closed note below.
      const allClosed = g.items.length > 0 && g.items.every(it => it.policy && !it.policy.eligible && it.policy.enforce);
      const groupIds = new Set(g.items.map(it => it.lineItemId));
      const hit = shipments.filter(s => s.items.some(it => groupIds.has(it.lineItemId)));
      const targetIds = hit.length ? hit.map(s => s.id) : ['pending'];
      const spans = hit.length > 1
        ? '<small class="odp__parcel-note">Covers items from more than one shipment — they go back together.</small>'
        : '';
      targetIds.forEach((id) => {
        const slot = shipmentSlot('data-shipment-actions', id);
        if (!slot) return;
        // Wrapped so the note travels with its button. Hiding the bare button
        // used to strand the note as an orphan caption under a card with no
        // action on it.
        if (allClosed) {
          const finalSale = g.items.every(it => it.policy.reason === 'not_allowed');
          const latest = g.items.reduce((m, it) => (it.policy.deadlineAt && (!m || it.policy.deadlineAt > m) ? it.policy.deadlineAt : m), null);
          slot.insertAdjacentHTML('beforeend',
            `<p class="odp__window-closed" data-return-closed>${finalSale
              ? (g.items.length === 1 ? 'This item is final sale and can\'t be returned.' : 'These items are final sale and can\'t be returned.')
              : `The return window for ${g.items.length === 1 ? 'this item' : 'these items'} closed${latest ? ` on ${escapeHtml(formatDeadline(latest, false))}` : ''}.`
            }${g.items.some(it => itemSupportable(it) && !supportBlocked(it))
              ? ` <button type="button" class="return-item__support-link" data-odp-action="make-it-right" data-group-index="${gi}">Something wrong with it? Ask our team to make it right</button>`
              : ' If something is wrong with the order, contact Birch support.'}</p>`);
          return;
        }
        // Earliest deadline among the group's still-open items, so the button
        // carries the date that matters.
        const soonest = g.items.reduce((m, it) => (it.policy && it.policy.eligible && it.policy.deadlineAt && (!m || it.policy.deadlineAt < m) ? it.policy.deadlineAt : m), null);
        const windowHint = soonest ? `<small class="odp__parcel-note odp__window-note">Return by ${escapeHtml(formatDeadline(soonest, false))}</small>` : '';
        // A shipment that already has a return (on the card, or started just
        // now) offers the REST of its items.
        const card = slot.closest('.odp__parcel');
        const started = submittedShipments.has(id) || !!(card && card.querySelector('.odp__return'));
        slot.insertAdjacentHTML('beforeend',
          `<div class="odp__return-action" data-return-action data-group-index="${gi}">` +
          `<button type="button" class="btn-outline" data-odp-action="return" data-group-index="${gi}">${started ? 'Return other items' : 'Request return'}</button>` +
          `${windowHint}${spans}</div>`);
      });
    });
  }

  async function loadManage(o) {
    if (o.cancelledAt) return;
    const isPaid = ['PAID', 'AUTHORIZED', 'PARTIALLY_PAID'].includes(o.displayFinancialStatus);
    const pending = pendingItems(o);

    if (isPaid && o.fulfillments.length) {
      if ((await refreshReturnActions(o)) === 'group_gift_contribution') return;
    }

    if (pending.length) {
      const slot = shipmentSlot('data-shipment-actions', 'pending');
      // The endpoint is order-wide and the policy engine decides what is
      // actually stoppable, so the panel still says our team confirms.
      const anyShipped = o.fulfillments.length > 0;
      if (slot) {
        const label = anyShipped
          ? (root.querySelectorAll('.odp__parcel--pending').length > 1 ? 'Cancel unshipped items' : 'Cancel these items')
          : 'Cancel order';
        const pc = policy && policy.cancel;
        if (pc && !pc.eligible && policy.enforce) {
          // The vendor/product rule has closed the window (or never opened it).
          // Say so instead of offering a button the server would refuse.
          slot.insertAdjacentHTML('beforeend', renderCancelClosed(pc));
        } else {
          const hint = pc && pc.eligible && pc.deadlineAt
            ? `<small class="odp__parcel-note odp__window-note">Cancel by ${escapeHtml(formatDeadline(pc.deadlineAt, true))}</small>`
            : '';
          slot.insertAdjacentHTML('beforeend',
            `<div class="odp__cancel-action"><button type="button" class="btn-outline" data-odp-action="cancel" data-partial="${anyShipped}">${label}</button>${hint}</div>`);
        }
      }
    }
  }

  // What replaces the cancel button once the rule says no. Names the deadline
  // that passed (or that this item has no online cancellation at all) and
  // points at returns, which is where a shipped-or-too-late item ends up.
  function renderCancelClosed(pc) {
    const closed = (pc.lines || []).filter(l => !l.eligible);
    const noneAllowed = closed.length && closed.every(l => l.reason === 'not_allowed');
    const latest = closed.reduce((m, l) => (l.deadlineAt && (!m || l.deadlineAt > m) ? l.deadlineAt : m), null);
    const what = noneAllowed
      ? "These items can't be cancelled online once ordered."
      : `The cancellation window for ${closed.length === 1 && closed[0].title ? `<em>${escapeHtml(closed[0].title)}</em>` : 'these items'} closed${latest ? ` on ${escapeHtml(formatDeadline(latest, true))}` : ''}.`;
    return `<p class="odp__window-closed" data-cancel-closed>${what} Once delivered, you can request a return instead — or contact Birch support if something is wrong with the order.</p>`;
  }

  function renderGiftContributionNotice(data) {
    const reg = data.groupGiftRegistryName
      ? `to <strong>${escapeHtml(data.groupGiftRegistryName)}</strong>'s group gift`
      : 'to a group gift on Birch';
    return `
      <div class="return-not-eligible return-not-eligible--gift">
        <p>This order was your contribution ${reg}. Contributions can't be returned individually — the funded item ships to the registrant.</p>
        <p><small>If something's wrong with the gift, reach out to the registrant or contact Birch support.</small></p>
      </div>`;
  }

  const RETURN_REASONS = [
    { value: 'DEFECTIVE', label: 'Defective' },
    { value: 'NOT_AS_DESCRIBED', label: 'Not as described' },
    { value: 'WRONG_ITEM', label: 'Wrong item' },
    { value: 'SIZE_TOO_SMALL', label: 'Too small' },
    { value: 'SIZE_TOO_LARGE', label: 'Too large' },
    { value: 'UNWANTED', label: 'No longer needed' },
    { value: 'OTHER', label: 'Other' },
  ];

  // Shopify's reasons, plus the ones Order Management adds while "make it
  // right" is on (Safety concern, Missing a part or piece) — those go first:
  // they matter most and lead to the team, not a return.
  function returnReasons() {
    const extra = (returnFlow && Array.isArray(returnFlow.extraReasons) ? returnFlow.extraReasons : [])
      .map(r => ({ value: r.id, label: r.label }));
    return [...extra, ...RETURN_REASONS];
  }

  // "Make it right" is on offer for this item at all (store switch + its rule).
  function itemSupportable(it) {
    return !!(returnFlow && returnFlow.makeItRight && it.makeItRight);
  }
  // Order Management's "Block duplicate requests" is on and this item already
  // went to the team in the last day — no second request. With it off (the
  // default) a repeat goes through and the customer is told of both.
  function supportBlocked(it) {
    return !!(returnFlow && returnFlow.makeItRightDedupe && it.makeItRight && it.makeItRight.requestedAt);
  }
  // …and suggested for this reason: a safety concern always, else the rule's
  // triggers, else any reason on a high-value item. The reason alone decides,
  // so the offer comes straight after it, before the condition question.
  function supportSuggested(it, reason) {
    if (!itemSupportable(it) || supportBlocked(it)) return false;
    const r = String(reason || '').toUpperCase();
    if (r === 'SAFETY' || teamOnlyReason(r)) return true;
    const m = it.makeItRight;
    return m.always || (!!r && m.triggers.includes(r));
  }
  // A reason with nothing to send back ("Never arrived") — Order Management
  // marks it teamOnly: the item goes to the team, never a return, so "Return
  // it" and the condition are never offered for it (OM refuses such a return).
  function teamOnlyReason(reason) {
    const r = String(reason || '').toUpperCase();
    return !!(returnFlow && Array.isArray(returnFlow.extraReasons)
      && returnFlow.extraReasons.some(x => x.teamOnly && String(x.id).toUpperCase() === r));
  }
  function supportRequestedNote(it) {
    const at = it.makeItRight && it.makeItRight.requestedAt;
    return at ? `<small class="return-item__window return-item__support">Our team has your request from ${escapeHtml(formatDeadline(at * 1000, true))} — they'll email you.</small>` : '';
  }

  function renderReturnFieldset(g, gi) {
    return `
      <fieldset class="return-fieldset" data-group-index="${gi}">
        ${g.items.map((it, ii) => {
          // The vendor/product rule's verdict for this line. Closed items stay
          // listed but can't be ticked, and say why — dropping them would read
          // as "we lost your item".
          const w = it.policy;
          const closed = !!(w && !w.eligible && w.enforce);
          const windowNote = !w ? ''
            : closed
              ? `<small class="return-item__window return-item__window--closed">${w.reason === 'not_allowed' ? 'Final sale — not returnable' : `Return window closed${w.deadlineAt ? ` on ${escapeHtml(formatDeadline(w.deadlineAt, false))}` : ''}`}</small>`
              : w.deadlineAt
                ? `<small class="return-item__window">Return by ${escapeHtml(formatDeadline(w.deadlineAt, false))}</small>`
                : '';
          return `
          <label class="return-item${it.isRegistryGift ? ' return-item--gift' : ''}${closed ? ' return-item--closed' : ''}">
            <input type="checkbox" name="item_${gi}_${ii}" value="${escapeHtml(it.fulfillmentLineItemId)}" data-max="${it.quantity}"${closed ? ' disabled' : ''} />
            <span>
              ${escapeHtml(it.title)}${optionsSuffix(it.lineItemId)} <small>(up to ${it.quantity})</small>
              ${it.isRegistryGift ? '<span class="return-item__gift-badge" title="Purchased through a Birch registry">Registry gift</span>' : ''}
              ${it.vendor ? `<small class="return-item__origin">Supplied by ${escapeHtml(it.vendor)}</small>` : ''}
              ${windowNote}
              ${!closed && conditionRestricted(it) ? `<small class="return-item__window">Returnable only if ${escapeHtml(conditionsPhrase(it.policy.conditions))}</small>` : ''}
              ${supportRequestedNote(it)}
              ${closed && itemSupportable(it) && !supportBlocked(it) ? `<button type="button" class="return-item__support-link" data-odp-action="make-it-right" data-group-index="${gi}" data-item-index="${ii}">Something wrong with it? Ask our team to make it right</button>` : ''}
            </span>
            <input type="number" min="1" max="${it.quantity}" value="1" name="qty_${gi}_${ii}" aria-label="Quantity to return for ${escapeHtml(it.title)}"${closed ? ' disabled' : ''} />
          </label>`;
        }).join('')}
        ${g.items.map((it, ii) => renderItemQuestions(g, gi, it, ii)).join('')}
        <div class="return-same-prompt" data-same-prompt hidden>
          <p data-same-text></p>
          <div class="return-same-prompt__actions">
            <button type="button" class="btn-primary btn-sm" data-same="yes">Use the same answers</button>
            <button type="button" class="btn-outline btn-sm" data-same="no">Answer each one</button>
          </div>
        </div>
        <div class="return-review" data-step="review" hidden>
          <p class="return-refund-method__label">Your request</p>
          <ul class="return-review__list" data-review-list></ul>
        </div>
        ${g.containsRegistryGift ? `
          <div class="return-refund-method return-refund-method--gift" data-step="refund" hidden>
            <p class="return-refund-method__label">Refund</p>
            <p class="return-refund-method__notice">
              ${g.registryNames.length === 1
                ? `This item was purchased through <strong>${escapeHtml(g.registryNames[0])}</strong>'s registry.`
                : g.registryNames.length > 1
                  ? `These items were purchased through <strong>${escapeHtml(g.registryNames.join(', '))}</strong>.`
                  : 'These items were purchased through a Birch registry.'}
              The refund will be issued as Birch Credit to the registrant — it won't go back to your card.
            </p>
          </div>` : `
          <div class="return-refund-method" data-refund-method data-step="refund" hidden>
            <p class="return-refund-method__label">How would you like to be refunded?</p>
            <label class="return-refund-method__option">
              <input type="radio" name="refund_method_${gi}" value="store_credit"${DEFAULT_REFUND_METHOD === 'store_credit' ? ' checked' : ''} />
              <span>Birch Credit <small>(faster)</small></span>
            </label>
            <label class="return-refund-method__option">
              <input type="radio" name="refund_method_${gi}" value="original"${DEFAULT_REFUND_METHOD === 'original' ? ' checked' : ''} />
              <span>Original payment method${originalPaymentLabel() ? ` <small>— ${escapeHtml(originalPaymentLabel())}</small>` : ''}</span>
            </label>
          </div>`}
        <textarea name="note_${gi}" placeholder="Optional note" aria-label="Additional details (optional)" data-step="note" hidden></textarea>
      </fieldset>`;
  }

  // One ticked item's questions, each revealed once the one before it is
  // answered: why → what condition → what it wants (refund, an exchange for the
  // same item, or keep it for Birch Credit — only the ones on offer for THIS
  // item). Answers are per item because they really differ ("this one's too
  // small, that one arrived damaged"), and the offers hang off them.
  // "Make it right" hangs off the reason alone, so where the reason suggests it
  // the next question is "our team makes it right, or return it?" — the team
  // ends the item's questions (no condition needed); "Return it" goes on to the
  // condition and what they'd like.
  function renderItemQuestions(g, gi, it, ii) {
    const askCondition = !!(returnFlow && returnFlow.askCondition);
    const canExchange = itemExchangeable(g, it);
    const canKeep = itemKeepable(g, it);
    const canSupport = itemSupportable(it);
    return `
      <div class="return-item-answers" data-item-block="${ii}" hidden>
        <div class="return-item-summary" data-item-summary hidden>
          <span data-summary-text></span>
          <button type="button" class="return-item-summary__edit" data-edit-item="${ii}" aria-label="Change the answers for ${escapeHtml(it.title)}">Edit</button>
        </div>
        <div class="return-item-answers__pending" data-item-pending hidden>${escapeHtml(it.title)}${optionsSuffix(it.lineItemId)} <small>— next</small></div>
        <div data-item-questions>
        <p class="return-item-answers__title" data-item-title>${escapeHtml(it.title)}${optionsSuffix(it.lineItemId)}</p>
        <div data-q="reason">
          <p class="return-refund-method__label return-step__label">Why are you returning it?</p>
          <select name="reason_${gi}_${ii}" aria-label="Reason for returning ${escapeHtml(it.title)}">
            <option value="" selected disabled>Select a reason</option>
            ${returnReasons().map(r => `<option value="${r.value}">${escapeHtml(r.label)}</option>`).join('')}
          </select>
        </div>
        <p class="return-safety" data-not-received hidden>Sorry it never arrived. There's nothing to send back — our team will look into the delivery and email you, usually with a replacement or a refund.</p>
        <p class="return-safety" data-not-received-contact hidden>Sorry it never arrived. There's nothing to send back, so it can't be returned here — please <a href="/pages/contact">contact us</a> and our team will look into the delivery.</p>
        ${canSupport ? `
          <div class="return-question" role="radiogroup" aria-label="Make it right for ${escapeHtml(it.title)}" data-q="support" hidden>
            <p class="return-safety" data-safety hidden><strong>For safety, please stop using it.</strong> We recommend letting our team look at this first — they'll make it right, usually with a replacement or a fix. You can still return it instead.</p>
            <p class="return-refund-method__label">What would you like?</p>
            <label class="return-refund-method__option">
              <input type="radio" name="path_${gi}_${ii}" value="support" />
              <span>Have our team make it right <small>(a replacement part, a new one or a fix — no need to send it back)</small></span>
            </label>
            <label class="return-refund-method__option">
              <input type="radio" name="path_${gi}_${ii}" value="return" />
              <span>Return it</span>
            </label>
          </div>` : ''}
        ${askCondition ? `
          <div class="return-question" role="radiogroup" aria-label="Condition of ${escapeHtml(it.title)}" data-q="condition" hidden>
            <p class="return-refund-method__label">What condition is it in?</p>
            ${returnFlow.conditions.map(c => `
              <label class="return-refund-method__option">
                <input type="radio" name="condition_${gi}_${ii}" value="${escapeHtml(c.id)}" />
                <span>${escapeHtml(c.label)}</span>
              </label>`).join('')}
          </div>` : ''}
        ${canExchange || canKeep ? `
          <div class="return-question" role="radiogroup" aria-label="What you'd like for ${escapeHtml(it.title)}" data-q="resolution" hidden>
            <p class="return-refund-method__label">What would you like?</p>
            <label class="return-refund-method__option">
              <input type="radio" name="resolution_${gi}_${ii}" value="refund" />
              <span>A refund</span>
            </label>
            ${canExchange ? `
            <label class="return-refund-method__option" data-option="exchange">
              <input type="radio" name="resolution_${gi}_${ii}" value="exchange" />
              <span>An exchange for the same item <small>(no charge — another size or colour if you like)</small></span>
            </label>
            <label class="return-exchange__pick" data-q="pick" hidden>
              <span>Send me</span>
              <select name="exchange_${gi}_${ii}" aria-label="Replacement for ${escapeHtml(it.title)}">
                ${it.exchange.options.map(o => `<option value="${escapeHtml(o.variantId)}"${o.current ? ' selected' : ''}>${escapeHtml(o.current ? `${o.title} (same as before)` : o.title)}</option>`).join('')}
              </select>
              <small class="return-exchange__note">Your replacement ships once we receive the item you're sending back.</small>
            </label>` : ''}
            ${canKeep ? `
            <label class="return-refund-method__option" data-option="keep">
              <input type="radio" name="resolution_${gi}_${ii}" value="keep" />
              <span>Keep it and get <strong data-keep-amount></strong> back as Birch Credit <small>(no need to send anything back)</small></span>
            </label>` : ''}
          </div>` : ''}
        <div class="return-item-next" data-item-next hidden>
          <button type="button" class="btn-outline btn-sm" data-next-item="${ii}"></button>
        </div>
        </div>
      </div>`;
  }

  function itemExchangeable(g, it) {
    return !!(returnFlow && returnFlow.exchanges && !g.containsRegistryGift
      && it.exchange && it.exchange.options && it.exchange.options.length);
  }
  function itemKeepable(g, it) {
    return !!(returnFlow && returnFlow.keepIt && !g.containsRegistryGift && it.keepIt);
  }

  // The keep-it credit for one item at this quantity, or null when its reason
  // or condition doesn't trigger the offer. The worker re-derives the figure.
  function keepItQuoteFor(it, qty, reason, condition) {
    const k = it.keepIt;
    if (!k) return null;
    if (!(k.triggers.includes(String(reason || '').toUpperCase()) || (condition && k.triggers.includes(condition)))) return null;
    const line = k.unitCreditMinor * Math.max(1, Math.min(it.quantity, qty || 1));
    const minor = k.maxCreditMinor == null ? line : Math.min(line, k.maxCreditMinor);
    return minor > 0 ? { minor, currency: k.currency } : null;
  }

  // Every ticked item's answers (each item's own — "use the same answers"
  // copies them into the other items' fields rather than standing in for them).
  function itemAnswers(form, g, gi) {
    const askCondition = !!(returnFlow && returnFlow.askCondition);
    const ticked = g.items.map((it, ii) => !!(form[`item_${gi}_${ii}`] && form[`item_${gi}_${ii}`].checked));
    const tickedIdx = ticked.map((t, ii) => (t ? ii : -1)).filter(ii => ii >= 0);
    const val = (name) => (form.querySelector(`[name="${name}"]:checked`) || {}).value || '';
    return {
      ticked, tickedIdx,
      items: tickedIdx.map(ii => {
        const it = g.items[ii];
        const reason = (form[`reason_${gi}_${ii}`] || {}).value || '';
        const condition = askCondition ? val(`condition_${gi}_${ii}`) : 'n/a';
        const qty = Number((form[`qty_${gi}_${ii}`] || {}).value);
        const keep = keepItQuoteFor(it, qty, reason, condition === 'n/a' ? '' : condition);
        const canExchange = itemExchangeable(g, it);
        const support = supportSuggested(it, reason);
        const teamOnly = teamOnlyReason(reason);
        // Never arrived, and the team can't take it for this item (its rule, or
        // already requested): nothing this form can file — it says to contact us.
        if (teamOnly && !support) {
          return { ii, it, qty, reason, path: '', condition: '', keep: null, canExchange, support, teamOnly, offers: false, resolution: '', done: false };
        }
        // Where the reason suggests the team: their pick between that and a
        // return — except a never-arrived item, which has only the team.
        // Going to the team ends the item's questions — no condition.
        const path = support ? (teamOnly ? 'support' : val(`path_${gi}_${ii}`)) : 'return';
        if (path === 'support') {
          return { ii, it, qty, reason, path, condition: '', keep: null, canExchange, support, teamOnly, offers: false, resolution: 'support', done: !!reason };
        }
        const returning = path === 'return';
        const offers = canExchange || !!keep;
        let resolution = !returning ? '' : offers ? val(`resolution_${gi}_${ii}`) : 'refund';
        if ((resolution === 'keep' && !keep) || (resolution === 'exchange' && !canExchange)) resolution = '';
        return { ii, it, qty, reason, path, condition: returning ? condition : '', keep, canExchange, support, offers, resolution,
          done: !!reason && returning && !!condition && !!resolution };
      }),
    };
  }

  // One item at a time. The item being answered shows its questions in order
  // (why → condition → what you'd like); an item the customer has confirmed
  // ("Next") folds to a one-line summary with Edit; the rest wait their turn.
  // After the first item, with others still to do, the form offers to copy its
  // answers to them. Once every ticked item is confirmed: a review of what each
  // will get, the refund method (only if something is refunded), the note and
  // submit. A single item needs no "Next" — answering it is confirming it.
  // Per-form state lives on the form: which items are confirmed, which one is
  // being edited, and whether the copy offer was already answered.
  function returnStepState(form) {
    if (!form._steps) form._steps = { confirmed: new Set(), editing: null, sameAsked: false };
    return form._steps;
  }

  function reasonLabelOf(id) {
    const r = returnReasons().find(x => x.value === id);
    return r ? r.label : id;
  }
  function conditionLabelOf(id) {
    const c = returnFlow && returnFlow.conditions ? returnFlow.conditions.find(x => x.id === id) : null;
    return c ? c.label : '';
  }
  function outcomeText(form, gi, x) {
    if (x.resolution === 'exchange') {
      const pick = form[`exchange_${gi}_${x.ii}`];
      const opt = pick && pick.selectedOptions && pick.selectedOptions[0];
      return `Exchange for ${opt ? opt.textContent.trim() : 'the same item'}`;
    }
    if (x.resolution === 'keep' && x.keep) return `Keep it — ${formatMoney((x.keep.minor / 100).toFixed(2), x.keep.currency)} Birch Credit`;
    if (x.resolution === 'support' && x.teamOnly) return 'Our team looks into the delivery';
    if (x.resolution === 'support') return 'Our team makes it right';
    return 'Refund';
  }
  function itemSummary(form, gi, x) {
    const parts = [reasonLabelOf(x.reason)];
    if (x.condition && x.condition !== 'n/a') parts.push(conditionLabelOf(x.condition));
    parts.push(outcomeText(form, gi, x));
    return `<span class="return-summary-text"><strong>${escapeHtml(x.it.title)}${x.qty > 1 ? ` × ${x.qty}` : ''}</strong><span>${escapeHtml(parts.join(' · '))}</span></span>`;
  }

  // Copy the first item's reason and condition to the others still to do, and
  // its outcome where that item offers it too (an exchange copies as "the same
  // item again"). Items whose answers then make sense are confirmed; the rest
  // stay open for their own outcome.
  function copyAnswers(form, g, gi, fromIi, toIdx) {
    const askCondition = !!(returnFlow && returnFlow.askCondition);
    const reason = (form[`reason_${gi}_${fromIi}`] || {}).value || '';
    const condition = askCondition ? (form.querySelector(`[name="condition_${gi}_${fromIi}"]:checked`) || {}).value || '' : '';
    const fromSupport = !!(form.querySelector(`[name="path_${gi}_${fromIi}"][value="support"]:checked`));
    // Did the first item's customer actually see "our team, or return it?" —
    // only then is there a pick to carry across. Without it, a target whose
    // own reason suggests the team (e.g. a safety concern on that item) must
    // be asked itself, safety notice and all — never silently "Return it".
    const fromItem = g.items[fromIi];
    const fromSawPath = !!fromItem && supportSuggested(fromItem, reason);
    const resolution = fromSupport ? 'support' : (form.querySelector(`[name="resolution_${gi}_${fromIi}"]:checked`) || {}).value || '';
    toIdx.forEach(ii => {
      if (form[`reason_${gi}_${ii}`]) form[`reason_${gi}_${ii}`].value = reason;
      // Same reason → same team-or-return offer; carry the pick across, but
      // only a pick the customer really made on the first item.
      form.querySelectorAll(`[name="path_${gi}_${ii}"]`).forEach(r => { r.checked = false; });
      if (fromSawPath) {
        const path = form.querySelector(`[name="path_${gi}_${ii}"][value="${fromSupport ? 'support' : 'return'}"]`);
        if (path) path.checked = true;
      }
      if (condition) {
        const c = form.querySelector(`[name="condition_${gi}_${ii}"][value="${condition}"]`);
        if (c) c.checked = true;
      }
      form.querySelectorAll(`[name="resolution_${gi}_${ii}"]`).forEach(r => { r.checked = false; });
    });
    // Outcomes need the offers that the copied reason/condition unlock.
    const a = itemAnswers(form, g, gi);
    const st = returnStepState(form);
    toIdx.forEach(ii => {
      const x = a.items.find(y => y.ii === ii);
      if (!x) return;
      // This item's reason offers the team but the first item never asked:
      // leave it open so the customer answers the question for it.
      if (x.support && !fromSawPath) return;
      const allowed = resolution === 'support' ? x.support
        : !x.offers ? resolution === 'refund' || !resolution
        : (resolution === 'refund') || (resolution === 'exchange' && x.canExchange) || (resolution === 'keep' && !!x.keep);
      if (x.offers && allowed && resolution !== 'support') {
        const r = form.querySelector(`[name="resolution_${gi}_${ii}"][value="${resolution}"]`);
        if (r) r.checked = true;
      }
      if (allowed) st.confirmed.add(ii);
    });
  }

  function syncReturnSteps(form, g, gi) {
    const show = (el, on) => { if (el) el.hidden = !on; };
    const st = returnStepState(form);
    const a = itemAnswers(form, g, gi);
    // Forget items no longer ticked; an unanswered item can't stay confirmed.
    [...st.confirmed].forEach(ii => {
      const x = a.items.find(y => y.ii === ii);
      if (!x || !x.done) st.confirmed.delete(ii);
    });
    if (st.editing != null && !a.ticked[st.editing]) st.editing = null;
    const single = a.items.length === 1;
    if (single && a.items[0].done) st.confirmed.add(a.items[0].ii);
    // A single item stays open once answered: its answers ARE the request.
    const active = st.editing != null ? st.editing
      : (a.items.find(x => !st.confirmed.has(x.ii)) || (single ? a.items[0] : {})).ii;
    const remaining = a.items.filter(x => x.ii !== active && !st.confirmed.has(x.ii));

    g.items.forEach((it, ii) => {
      const block = form.querySelector(`[data-item-block="${ii}"]`);
      show(block, a.ticked[ii]);
      if (!block || !a.ticked[ii]) return;
      const x = a.items.find(y => y.ii === ii);
      const isActive = ii === active;
      const isDone = st.confirmed.has(ii) && !isActive;
      // A single item's block only ever shows its questions.
      show(block, isActive || !single);
      show(block.querySelector('[data-item-questions]'), isActive);
      show(block.querySelector('[data-item-summary]'), isDone && !single);
      show(block.querySelector('[data-item-pending]'), !isActive && !isDone);
      block.classList.toggle('return-item-answers--active', isActive && !single);
      if (isDone) block.querySelector('[data-summary-text]').innerHTML = itemSummary(form, gi, x);
      if (!isActive) return;

      show(block.querySelector('[data-item-title]'), !single);
      show(block.querySelector('[data-q="reason"]'), true);
      // Make it right (when this reason suggests it), then — as the
      // alternative — the condition, which leads on to a return.
      // Where the reason suggests it: our team, or return it. Only a return
      // goes on to the condition.
      const supportQ = block.querySelector('[data-q="support"]');
      // Never arrived: no choice to make — the note, and straight to the team.
      show(supportQ, !!x.reason && x.support && !x.teamOnly);
      show(block.querySelector('[data-not-received]'), !!x.teamOnly && x.support);
      show(block.querySelector('[data-not-received-contact]'), !!x.teamOnly && !x.support);
      if (!x.support && supportQ) supportQ.querySelectorAll('input').forEach(r => { r.checked = false; });
      show(block.querySelector('[data-safety]'), String(x.reason).toUpperCase() === 'SAFETY' && x.support);
      const returning = !!x.reason && x.path === 'return';
      show(block.querySelector('[data-q="condition"]'), returning);
      const resQ = block.querySelector('[data-q="resolution"]');
      const keepOpt = block.querySelector('[data-option="keep"]');
      show(keepOpt, !!x.keep);
      if (!x.keep && keepOpt) keepOpt.querySelector('input').checked = false;
      const amount = block.querySelector('[data-keep-amount]');
      if (amount && x.keep) amount.textContent = formatMoney((x.keep.minor / 100).toFixed(2), x.keep.currency);
      show(resQ, returning && !!x.condition && x.offers);
      show(block.querySelector('[data-q="pick"]'), x.resolution === 'exchange');
      const next = block.querySelector('[data-item-next]');
      show(next, !single && x.done);
      if (next) next.querySelector('button').textContent = remaining.length
        ? `Next: ${remaining[0].it.title}`
        : 'Review your request';
    });

    // The copy offer: once the first item is confirmed, with others still to do.
    const confirmedOne = a.items.find(x => st.confirmed.has(x.ii));
    const toDo = a.items.filter(x => !st.confirmed.has(x.ii));
    const offerSame = !st.sameAsked && st.editing == null && !!confirmedOne && toDo.length >= 1;
    show(form.querySelector('[data-same-prompt]'), offerSame);
    if (offerSame) {
      form.querySelector('[data-same-text]').textContent =
        `Use the same answers for the other ${toDo.length === 1 ? 'item' : `${toDo.length} items`} (${toDo.map(x => x.it.title).join(', ')})? You can change any of them after.`;
      // While offered, hold the next item closed so the choice comes first.
      toDo.forEach(x => {
        const block = form.querySelector(`[data-item-block="${x.ii}"]`);
        show(block.querySelector('[data-item-questions]'), false);
        show(block.querySelector('[data-item-pending]'), true);
        block.classList.remove('return-item-answers--active');
      });
    }

    const allDone = a.items.length > 0 && a.items.every(x => st.confirmed.has(x.ii)) && st.editing == null;
    const review = form.querySelector('[data-step="review"]');
    show(review, allDone && !single);
    if (allDone && !single) {
      form.querySelector('[data-review-list]').innerHTML = a.items.map(x =>
        `<li>${itemSummary(form, gi, x)} <button type="button" class="return-item-summary__edit" data-edit-item="${x.ii}">Edit</button></li>`).join('');
      // The summaries live in the review now; hide the per-item copies.
      a.items.forEach(x => show(form.querySelector(`[data-item-block="${x.ii}"]`), false));
    }
    const refunding = a.items.some(x => x.resolution === 'refund');
    form.querySelectorAll('[data-step="refund"]').forEach(el => show(el, allDone && refunding));
    form.querySelectorAll('[data-step="note"]').forEach(el => show(el, allDone));
    // The team needs to know what's wrong; for a return it stays optional.
    const supporting = a.items.some(x => x.resolution === 'support');
    const supportTitles = a.items.filter(x => x.resolution === 'support').map(x => x.it.title);
    form.querySelectorAll('[data-step="note"]').forEach(el => {
      el.placeholder = supporting
        ? `Tell our team what's wrong${a.items.length > supportTitles.length ? ` with ${supportTitles.join(', ')}` : ''} (required) — what happened, which part`
        : 'Optional note';
      el.required = supporting;
    });
    const submit = form.querySelector('button[type="submit"]');
    if (submit) {
      submit.disabled = !allDone;
      const count = (r) => a.items.filter(x => x.resolution === r).length;
      const all = (r) => a.items.length > 0 && count(r) === a.items.length;
      const keepTotal = a.items.reduce((n, x) => n + (x.resolution === 'keep' && x.keep ? x.keep.minor : 0), 0);
      const cur = (a.items.find(x => x.keep) || {}).keep;
      const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
      const mixed = [
        count('refund') && plural(count('refund'), 'return', 'returns'),
        count('exchange') && plural(count('exchange'), 'exchange', 'exchanges'),
        count('keep') && `${count('keep')} kept for credit`,
        count('support') && `${count('support')} to our team`,
      ].filter(Boolean);
      submit.textContent = !allDone ? 'Submit return request'
        : all('keep') && cur ? `Get ${formatMoney((keepTotal / 100).toFixed(2), cur.currency)} Birch Credit`
        : all('support') ? 'Send to our team'
        : all('exchange') ? 'Submit exchange request'
        : mixed.length > 1 ? `Submit: ${mixed.join(', ')}`
        : 'Submit return request';
    }
  }

  // The buttons that move between items: Next, Edit, and the copy offer.
  function wireReturnSteps(form, g, gi) {
    form.addEventListener('click', (ev) => {
      const t = ev.target.closest('button');
      if (!t) return;
      const st = returnStepState(form);
      if (t.dataset.nextItem !== undefined) {
        st.confirmed.add(Number(t.dataset.nextItem));
        st.editing = null;
      } else if (t.dataset.editItem !== undefined) {
        const ii = Number(t.dataset.editItem);
        st.confirmed.delete(ii);
        st.editing = ii;
      } else if (t.dataset.same) {
        st.sameAsked = true;
        if (t.dataset.same === 'yes') {
          const a = itemAnswers(form, g, gi);
          const from = a.items.find(x => st.confirmed.has(x.ii));
          if (from) copyAnswers(form, g, gi, from.ii, a.items.filter(x => !st.confirmed.has(x.ii)).map(x => x.ii));
        }
      } else {
        return;
      }
      syncReturnSteps(form, g, gi);
      const focus = form.querySelector('.return-item-answers--active, [data-step="review"]:not([hidden])');
      if (focus) focus.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  // "Make it right": no return — Order Management emails the support team,
  // who reply to the customer. Resolves to { ok, html }; never throws.
  async function requestMakeItRight(items, note) {
    const { ok, data: resp } = await call(`/orders/${orderId}/make-it-right`, { method: 'POST', body: {
      items: items.map(({ lineItemId, quantity, reason }) => ({ lineItemId, quantity, reason })),
      note,
    } });
    const names = items.map(x => x.title + (x.quantity > 1 ? ` × ${x.quantity}` : '')).join(', ');
    if (ok && resp && resp.ok) {
      // A repeat (allowed unless "Block duplicate requests" is on): say the
      // earlier request is still with the team and this one went too.
      const earlier = Array.isArray(resp.earlier) ? resp.earlier : [];
      const earlierNote = earlier.length
        ? `<p>Our team already had your earlier request for ${escapeHtml(earlier.map(e => `${e.title} (${formatDeadline(e.requestedAt * 1000, true)})`).join(', '))} — we've sent this one too, so they have both.</p>`
        : '';
      return { ok: true, html: `<p class="order-card__panel-title">Sent to our team</p>${earlierNote}<p>We've asked our team to make it right for ${escapeHtml(names)}. They'll email you, usually within a business day.${resp.safety ? ' Please stop using it until you hear from them.' : ''} Nothing has been returned or refunded.</p>` };
    }
    const already = resp && Array.isArray(resp.lines) && resp.lines.some(l => l.reason === 'already_requested');
    return { ok: false, html: `<p class="return-summary return-summary--fail">${already
      ? `Our team already has a request for ${escapeHtml(names)} from the last day — they'll be in touch.`
      : `We couldn't send your request for ${escapeHtml(names)}. Please email Birch support, or try again in a moment.`}</p>` };
  }

  // The standalone "make it right" form, for items the customer can no longer
  // return (window closed, final sale) — opened from the closed note or the
  // item's link. One reason and description for the request.
  function renderMakeItRightForm(g, gi, only) {
    const items = g.items.map((it, ii) => ({ it, ii })).filter(({ it }) => itemSupportable(it) && !supportBlocked(it));
    return `
      <form data-mir-form>
        <p class="order-card__panel-title">Ask our team to make it right</p>
        <p><small>A replacement part, a new one or a fix — the brand's warranty may cover it even after the return window. Nothing is returned or refunded unless the team arranges it.</small></p>
        <fieldset class="return-fieldset">
          ${items.map(({ it, ii }) => `
            <label class="return-item">
              <input type="checkbox" name="mir_item_${ii}"${only === ii || items.length === 1 ? ' checked' : ''} />
              <span>${escapeHtml(it.title)}${optionsSuffix(it.lineItemId)} <small>(up to ${it.quantity})</small></span>
              <input type="number" min="1" max="${it.quantity}" value="1" name="mir_qty_${ii}" aria-label="Quantity for ${escapeHtml(it.title)}" />
            </label>`).join('')}
          <p class="return-refund-method__label return-step__label">What's the problem?</p>
          <select name="mir_reason" aria-label="What's the problem">
            <option value="" selected disabled>Select one</option>
            ${returnReasons().filter(r => !['SIZE_TOO_SMALL', 'SIZE_TOO_LARGE', 'UNWANTED'].includes(r.value)).map(r => `<option value="${r.value}">${escapeHtml(r.label)}</option>`).join('')}
          </select>
          <p class="return-safety" data-mir-safety hidden><strong>For safety, please stop using it</strong> until our team has been in touch.</p>
          <textarea name="mir_note" required placeholder="Tell our team what's wrong (required) — what happened, which part" aria-label="What's wrong"></textarea>
        </fieldset>
        <div class="cancel-form__actions">
          <button type="button" class="btn-outline" data-odp-action="return-dismiss">Nevermind</button>
          <button type="submit" class="btn-primary">Send to our team</button>
        </div>
      </form>`;
  }

  // "Keep it" for one item: no return — the worker issues the Birch Credit at
  // once. Resolves to { ok, title, amount?, currency?, message? }; never throws.
  async function keepItem(x, note) {
    const { ok, data: resp } = await call(`/orders/${orderId}/keep-it`, { method: 'POST', body: {
      items: [{ lineItemId: x.it.lineItemId, quantity: x.qty }],
      reason: x.reason,
      condition: x.condition === 'n/a' ? undefined : x.condition,
      note: note || undefined,
    } });
    if (ok && resp && resp.ok) return { ok: true, title: x.it.title, amount: resp.amount, currency: resp.currency };
    const line = resp && Array.isArray(resp.lines) ? resp.lines[0] : null;
    return {
      ok: false,
      title: x.it.title,
      message: line && line.reason === 'already_credited'
        ? `${x.it.title} already had a keep-it credit.`
        : `We couldn't add the credit for ${x.it.title} — nothing was credited. You can request a return for it instead.`,
    };
  }

  // The item's rule accepts returns in only some conditions (e.g. unopened),
  // the rules are enforced, and the form asks the condition — worth saying up
  // front. With the question off no condition is sent, so nothing can be
  // refused for one, and the hint would only mislead.
  function conditionRestricted(it) {
    if (!returnFlow || !returnFlow.askCondition) return false;
    const all = returnFlow.conditions ? returnFlow.conditions.length : 0;
    return !!(it.policy && it.policy.enforce && Array.isArray(it.policy.conditions)
      && all && it.policy.conditions.length < all);
  }

  // ["unopened", "damaged"] → "unopened, in its original packaging or arrived
  // damaged or defective" — the flow's own labels, lowercased.
  function conditionsPhrase(ids) {
    const labels = (ids || []).map(id => {
      const c = returnFlow && returnFlow.conditions ? returnFlow.conditions.find(x => x.id === id) : null;
      return (c ? c.label : id).toLowerCase();
    });
    return labels.length <= 1 ? (labels[0] || '') : `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
  }

  // Invoice, moved here from the orders-list card: the list is a summary, and a
  // full financial document belongs with the order it documents. Rendered up
  // front (hidden) rather than on demand so the print handler always has a real
  // node to hoist, and so print-from-keyboard can't race a render.
  function renderInvoiceSection(o, isPaid) {
    return `
      <div class="odp__section odp__invoice-section">
        <h2 class="odp__section-title">Invoice</h2>
        <div class="odp__action-buttons">
          <button type="button" class="btn-outline" data-odp-action="invoice" aria-expanded="false" aria-controls="odp-invoice">View invoice</button>
        </div>
        <div class="odp__invoice" id="odp-invoice" data-odp-invoice hidden>${renderInvoice(o, isPaid)}</div>
      </div>`;
  }

  // How the order was paid and refunded — one row per source, then each refund
  // with where it went, then Shopify's net. Every amount is Shopify's own.
  function renderInvoicePayments(currency) {
    const p = orderPayments;
    if (!p || (!p.paid.length && !(p.refunds || []).length)) return '';
    const money = (a) => formatMoney(a, currency);
    const refunds = p.refunds || [];
    return `
        <table class="invoice__items invoice__payments">
          <thead><tr><th colspan="2">Payment</th></tr></thead>
          <tbody>
            ${p.paid.map(x => `<tr><td>Paid with ${escapeHtml(x.label)}</td><td>${money(x.amount)}</td></tr>`).join('')}
            ${refunds.map(r => `<tr class="invoice__refund"><td>Refunded ${escapeHtml(formatDate(r.at, { month: 'short', day: 'numeric', year: 'numeric' }))}${r.to && r.to.length ? ` to ${escapeHtml(r.to.map(x => r.to.length > 1 ? `${x.label} (${money(x.amount)})` : x.label).join(' + '))}` : ''}</td><td>&minus;${money(r.amount)}</td></tr>`).join('')}
          </tbody>
          ${refunds.length && p.netPaid != null ? `<tfoot><tr class="invoice__items-total"><td><strong>Net paid</strong></td><td><strong>${money(p.netPaid)}</strong></td></tr></tfoot>` : ''}
        </table>`;
  }

  // Every figure on the invoice is Shopify's own — we never compute money.
  //
  // Shopify's `subtotalPriceSet` is ALREADY net of discounts, and
  // subtotal + shipping + tax lands exactly on `totalPriceSet`, so the column
  // reconciles with no arithmetic from us. Listing a separate "Discount" row
  // BELOW that subtotal is what breaks it: the saving gets counted twice and
  // #1017 reads 56.80 − 14.20 + 8.00 = 50.60 against a stated total of 64.80.
  //
  // Shopify has no "subtotal before discounts" field, so rather than derive one,
  // the discount is shown as a note against the subtotal it is already baked
  // into. Amounts are passed through as Shopify's own strings — parseFloat is
  // used only to decide whether a row is worth showing, never to produce a
  // number we display.
  // PDP link for a line, or null when there is nothing to land on: the product
  // was deleted (product null), is DRAFT/ARCHIVED (404 on the storefront —
  // UNLISTED still resolves by direct URL, so it stays linkable), or the worker
  // predates the handle field. `?variant=` pins the exact variant bought.
  function productUrl(line) {
    const product = line && line.product;
    if (!product || !product.handle) return null;
    if (product.status && product.status !== 'ACTIVE' && product.status !== 'UNLISTED') return null;
    const variantId = line.variant && line.variant.id ? String(line.variant.id).split('/').pop() : '';
    return `/products/${encodeURIComponent(product.handle)}${variantId ? `?variant=${encodeURIComponent(variantId)}` : ''}`;
  }

  // Wraps a rendered image / title in the PDP link when there is one; the
  // markup is untouched otherwise so the row reads the same either way.
  function linkTo(url, inner, className) {
    return url ? `<a href="${escapeHtml(url)}" class="${className}">${inner}</a>` : inner;
  }

  // Shopify's own line total. Falls back to unit × quantity only if the worker
  // predates `originalTotalSet` — a theme deploy shouldn't have to wait on it.
  function lineTotal(line) {
    const shopify = line.originalTotalSet?.shopMoney.amount;
    if (shopify) return shopify;
    return String(parseFloat(line.originalUnitPriceSet.shopMoney.amount) * line.quantity);
  }

  function orderTotals(o) {
    const raw = (set) => set?.shopMoney.amount ?? null;
    const ifPositive = (set) => {
      const amount = raw(set);
      return amount && parseFloat(amount) > 0 ? amount : null;
    };
    return {
      subtotal: raw(o.subtotalPriceSet),
      discounts: ifPositive(o.totalDiscountsSet),
      // A shipped order always lists its shipping, "Free" at zero, like the
      // order confirmation email; an order with nothing to ship has no row.
      shipping: ifPositive(o.totalShippingPriceSet) ?? (o.shippingAddress ? '0' : null),
      tax: ifPositive(o.totalTaxSet),
      total: raw(o.totalPriceSet),
      // A deposit order (pre-order partial payment) is PARTIALLY_PAID: part of
      // the total was charged at checkout and the rest is still owed. Without
      // these the page called the whole total "Total paid". Null on a worker
      // that predates the fields, which just drops the two rows.
      received: ifPositive(o.totalReceivedSet),
      outstanding: ifPositive(o.totalOutstandingSet),
    };
  }

  // "Total paid" only when nothing is still owed; otherwise the total is just the
  // total, and paidRows() says how much of it has been paid and what remains.
  // The status check covers a worker that predates the amounts.
  function isFullyPaid(o, t, isPaid) {
    return isPaid && !t.outstanding && o.displayFinancialStatus !== 'PARTIALLY_PAID';
  }

  function totalLabel(o, t, isPaid) {
    return isFullyPaid(o, t, isPaid) ? 'Total paid' : 'Total';
  }

  function paidRows(t, currency, { cols = 1, rowClass = '' } = {}) {
    if (!t.outstanding) return '';
    const label = (text) => (cols > 1 ? `<td colspan="${cols}">${text}</td>` : `<td>${text}</td>`);
    return `
      ${t.received ? `<tr class="${rowClass} ${rowClass}--paid">${label('Paid')}<td>${formatMoney(t.received, currency)}</td></tr>` : ''}
      <tr class="${rowClass} ${rowClass}--due">${label('Balance due')}<td>${formatMoney(t.outstanding, currency)}</td></tr>`;
  }

  function shippingAmount(amount, currency) {
    return parseFloat(amount) > 0 ? formatMoney(amount, currency) : 'Free';
  }

  function renderInvoice(o, isPaid) {
    const currency = o.currencyCode;
    const shopName = root.dataset.shopName || '';
    const shopAddress = root.dataset.shopAddress || '';
    const shopEmail = root.dataset.shopEmail || '';
    const rows = o.lineItems.nodes.map(line => `
        <tr>
          <td>${escapeHtml(line.title)}${lineOptionsText(line) ? `<br><small>${escapeHtml(lineOptionsText(line))}</small>` : ''}${line.sku ? ` <small>${escapeHtml(line.sku)}</small>` : ''}</td>
          <td>${line.quantity}</td>
          <td>${formatMoney(line.originalUnitPriceSet.shopMoney.amount, currency)}</td>
          <td>${formatMoney(lineTotal(line), currency)}</td>
        </tr>`).join('');
    const t = orderTotals(o);
    return `
      <div class="invoice">
        <header class="invoice__head">
          <div>
            ${shopName ? `<h3 class="invoice__shop">${escapeHtml(shopName)}</h3>` : ''}
            ${shopAddress ? `<p class="invoice__shop-meta">${escapeHtml(shopAddress)}</p>` : ''}
            ${shopEmail ? `<p class="invoice__shop-meta">${escapeHtml(shopEmail)}</p>` : ''}
          </div>
          <div class="invoice__meta">
            <p class="invoice__meta-line"><strong>Invoice ${escapeHtml(o.name)}</strong></p>
            <p class="invoice__meta-line">Date: ${formatDate(o.createdAt, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
            <p class="invoice__meta-line">Status: ${escapeHtml(titleCase(o.displayFinancialStatus))}</p>
          </div>
        </header>

        <div class="invoice__addresses">
          ${o.billingAddress ? `<div><p class="invoice__addr-title">Billed to</p>${renderAddress(o.billingAddress)}</div>` : ''}
          ${o.shippingAddress ? `<div><p class="invoice__addr-title">Shipped to</p>${renderAddress(o.shippingAddress)}</div>` : ''}
        </div>

        <table class="invoice__items">
          <thead>
            <tr><th>Item</th><th>Qty</th><th>Unit</th><th>Total</th></tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            ${t.subtotal ? `<tr><td colspan="3">Subtotal${t.discounts ? ` <small>after ${formatMoney(t.discounts, currency)} in discounts</small>` : ''}</td><td>${formatMoney(t.subtotal, currency)}</td></tr>` : ''}
            ${t.shipping ? `<tr><td colspan="3">Shipping <small data-shipping-method hidden></small></td><td>${shippingAmount(t.shipping, currency)}</td></tr>` : ''}
            ${t.tax ? `<tr><td colspan="3">Tax</td><td>${formatMoney(t.tax, currency)}</td></tr>` : ''}
            <tr class="invoice__items-total"><td colspan="3"><strong>${totalLabel(o, t, isPaid)}</strong></td><td><strong>${formatMoney(t.total, currency)}</strong></td></tr>
            ${paidRows(t, currency, { cols: 3, rowClass: 'invoice__items-balance' })}
          </tfoot>
        </table>
        ${renderInvoicePayments(currency)}

        <div class="invoice__actions">
          <button type="button" class="btn-outline" data-odp-action="invoice-print">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
            Print / Save as PDF
          </button>
          <button type="button" class="btn-outline" data-odp-action="invoice-close">Hide invoice</button>
        </div>
      </div>`;
  }

  function renderPaymentSummary(o, isPaid) {
    const currency = o.currencyCode;
    // Same reconciled figures as the invoice — see orderTotals(). Two different
    // subtotals for one order on one page would be worse than either alone.
    const t = orderTotals(o);
    return `
      <table class="odp__summary-table">
        <tbody>
          ${t.subtotal ? `<tr><td>Subtotal${t.discounts ? `<small class="odp__summary-note">after ${formatMoney(t.discounts, currency)} in discounts</small>` : ''}</td><td>${formatMoney(t.subtotal, currency)}</td></tr>` : ''}
          ${t.shipping ? `<tr><td>Shipping<small class="odp__summary-note" data-shipping-method hidden></small></td><td>${shippingAmount(t.shipping, currency)}</td></tr>` : ''}
          ${t.tax ? `<tr><td>Tax</td><td>${formatMoney(t.tax, currency)}</td></tr>` : ''}
        </tbody>
        <tfoot>
          <tr class="odp__summary-total">
            <td>${totalLabel(o, t, isPaid)}</td>
            <td class="${isFullyPaid(o, t, isPaid) ? 'is-paid' : ''}">${formatMoney(t.total, currency)}</td>
          </tr>
          ${paidRows(t, currency, { rowClass: 'odp__summary-balance' })}
        </tfoot>
      </table>`;
  }

  function renderAddress(addr) {
    const name = [addr.firstName, addr.lastName].filter(Boolean).join(' ');
    const line2 = [addr.city, addr.provinceCode, addr.zip].filter(Boolean).join(', ');
    return `
      <address class="odp__address">
        ${name ? `<p>${escapeHtml(name)}</p>` : ''}
        ${addr.address1 ? `<p>${escapeHtml(addr.address1)}</p>` : ''}
        ${addr.address2 ? `<p>${escapeHtml(addr.address2)}</p>` : ''}
        ${line2 ? `<p>${escapeHtml(line2)}</p>` : ''}
        ${addr.country ? `<p>${escapeHtml(addr.country)}</p>` : ''}
      </address>`;
  }

  // ── Async enrichments ────────────────────────────────────────────────────

  async function upgradeDeliveryStatus(o) {
    const progressEl = root.querySelector('[data-odp-progress]');
    const shippedStep = progressEl && progressEl.querySelector('[data-progress-step="shipped"].is-complete');
    if (!shippedStep) return;
    const { ok, data } = await call(`/orders/${orderId}/tracking`);
    if (!ok || !data?.tracking?.length) return;
    // EVERY shipment must be delivered, and nothing may still be outstanding.
    // `.some()` here meant a split order flipped to "Delivered" as soon as the
    // first box landed, while the customer was still waiting on the rest.
    const isDelivered = t => (t.status || '').toUpperCase() === 'DELIVERED' || !!t.deliveredAt;
    const delivered = data.tracking.every(isDelivered) && pendingItems(o).length === 0;
    const delStep = progressEl.querySelector('[data-progress-step="delivered"]');
    if (!delivered || !delStep) return;
    delStep.classList.add('is-complete');
    const deliveredEvent = data.tracking
      .flatMap(t => t.events || [])
      .find(ev => (ev.status || '').toUpperCase() === 'DELIVERED');
    if (deliveredEvent?.at) {
      const time = document.createElement('time');
      time.className = 'odp-progress__time';
      time.textContent = formatDate(deliveredEvent.at, { month: 'short', day: 'numeric' });
      delStep.appendChild(time);
    }
  }

  // Live carrier status enriches the cards already on screen rather than
  // re-rendering a separate tracking list — the card is the single place the
  // customer looks for a shipment.
  async function loadTracking() {
    if (!root.querySelector('[data-shipment]')) return;
    const { ok, data } = await call(`/orders/${orderId}/tracking`);
    if (!ok || !data?.tracking?.length) return;
    // Collapse parcels back to their shipment, so a multi-package fulfillment
    // updates one card.
    const byShipment = new Map();
    data.tracking.forEach(t => {
      const key = t.fulfillmentId || '';
      if (!key) return;
      if (!byShipment.has(key)) byShipment.set(key, []);
      byShipment.get(key).push(t);
    });
    byShipment.forEach((parcels, id) => {
      const card = shipmentCard(id);
      if (!card) return;
      const live = parcels.find(p => p.status) || parcels[0];
      const state = fulfillmentState({ status: live.status, deliveredAt: live.deliveredAt });
      const badge = card.querySelector('[data-shipment-state]');
      if (state && badge) {
        badge.textContent = state.label;
        badge.className = `tracking-block__state tracking-block__state--${state.tone}`;
      }
      const detail = card.querySelector('[data-shipment-detail]');
      if (!detail) return;
      const eta = live.estimatedDelivery
        ? `<p class="odp__parcel-eta">Estimated delivery ${escapeHtml(formatDate(live.estimatedDelivery, { month: 'short', day: 'numeric' }))}</p>`
        : '';
      const events = parcels.flatMap(p => p.events || []);
      detail.innerHTML = eta + (events.length ? renderTimeline(events) : '');
    });
  }

  function renderTimeline(events) {
    const rows = events.map(ev => `
      <li>
        <time>${escapeHtml(formatDate(ev.at, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }))}</time>
        &mdash; ${escapeHtml(ev.description || ev.status || '')}
        ${ev.location ? ` <small>(${escapeHtml(ev.location)})</small>` : ''}
      </li>`).join('');
    return `
      <details class="odp__timeline">
        <summary>Tracking history <small>(${events.length})</small></summary>
        <ol class="tracking-timeline">${rows}</ol>
      </details>`;
  }

  // Shipment ids are Shopify GIDs (slashes, colons), so match on the dataset in
  // JS rather than building an attribute selector that would need escaping.
  function shipmentCard(id) {
    return [...root.querySelectorAll('[data-shipment]')].find(el => el.dataset.shipment === id) || null;
  }
  function shipmentSlot(attr, id) {
    return [...root.querySelectorAll(`[${attr}]`)].find(el => el.getAttribute(attr) === id) || null;
  }
  // Every action panel belongs to the card its button sits in, so proximity is
  // the lookup — no id plumbing, and two cards can be open at once.
  function panelFor(btn) {
    const card = btn.closest('.odp__parcel');
    return card ? card.querySelector('[data-shipment-panel]') : null;
  }

  // First return button that is still actionable. Retiring a group hides its
  // WRAPPER (so the note goes with it), which leaves the button itself matching
  // a plain selector — callers that hand off to "some" return button would
  // otherwise open a form for a shipment already on its way back.
  function firstLiveReturnButton() {
    return [...root.querySelectorAll('button[data-odp-action="return"][data-group-index]')]
      .find(b => !b.closest('[data-return-action][hidden]')) || null;
  }

  const RETURN_STATUS_LABELS = {
    pending: 'Return requested', requested: 'Return requested',
    approved: 'Return approved', label_sent: 'Return label ready',
    in_transit: 'Return in transit', received: 'Return received',
    refunded: 'Refunded', rejected: 'Return declined',
    cancelled: 'Return cancelled', closed: 'Return closed',
    complete: 'Return complete', partial: 'Return in progress',
  };

  async function loadCancellation(o, managed) {
    const { ok, data } = await call('/cancellations');
    if (!ok || !data?.cancellations) return;
    const info = data.cancellations[orderId];
    if (!info) return;
    // The cancel button lives inside a loadManage()-rendered block.
    await managed;
    const banner = root.querySelector('[data-cancellation]');
    const cancelBtn = root.querySelector('button[data-odp-action="cancel"]');
    const isCancelled = !!o.cancelledAt;
    if (info.status === 'pending' && !isCancelled) {
      banner.hidden = false;
      banner.innerHTML = `<p><strong>Cancellation requested.</strong> Awaiting review by our team.<br><small>Reason: ${escapeHtml(info.customerReason)}</small></p>`;
      if (cancelBtn) cancelBtn.hidden = true;
    } else if (info.status === 'cancel_submitted' && !isCancelled) {
      // orderCancel runs asynchronously on Shopify — this covers the short window
      // between us submitting the cancel and the order actually being cancelled.
      banner.hidden = false;
      banner.innerHTML = `<p><strong>Cancellation submitted.</strong> We're finalizing it with the store — your refund will follow shortly once it's confirmed.</p>`;
      if (cancelBtn) cancelBtn.hidden = true;
    } else if (info.status === 'awaiting_acceptance' && !isCancelled) {
      banner.hidden = false;
      banner.innerHTML = `<p><strong>Cancellation requested.</strong> We'll process it as soon as the supplier picks up your order. If it ships first, you'll be able to return it instead.</p>`;
      if (cancelBtn) cancelBtn.hidden = true;
    } else if (info.status === 'awaiting_supplier' && !isCancelled) {
      banner.hidden = false;
      banner.innerHTML = `<p><strong>Cancellation requested.</strong> We've asked the supplier to cancel this order — they usually respond within about two business days, and we'll email you as soon as they do. If they've already started shipping you'll be able to return it instead; if it never ships, it's cancelled automatically.</p>`;
      if (cancelBtn) cancelBtn.hidden = true;
    } else if (info.status === 'approved') {
      banner.hidden = false;
      banner.innerHTML = `<p><strong>Cancellation approved.</strong> Your refund is being processed.</p>`;
      if (cancelBtn) cancelBtn.hidden = true;
    } else if (info.status === 'denied' && !isCancelled) {
      banner.hidden = false;
      if (cancelBtn) cancelBtn.hidden = true;
      // No "Request a return instead" here (owner, 2026-10-08): a denied
      // cancellation says what happens next; a delivered item's own "Request
      // return" button sits on its shipment.
      const returnCta = '';
      if (info.deniedBy === 'supplier') {
        // Collective supplier declined the cancellation ("Request declined") — the
        // item will ship, so steer to a return rather than showing a bare denial.
        const detail = info.deniedReason ? `<br><small>${escapeHtml(info.deniedReason)}</small>` : '';
        banner.innerHTML = `<p><strong>This order can no longer be cancelled.</strong> The supplier has already begun preparing it, so it'll be on its way soon — you can request a return once it arrives.${detail}</p>${returnCta}`;
      } else {
        banner.innerHTML = `<p><strong>Cancellation denied.</strong><br><small>Reason: ${escapeHtml(info.deniedReason || 'No reason provided')}</small></p>${returnCta}`;
      }
    } else if (info.status && info.status !== 'pending' && info.status !== 'denied' && !isCancelled) {
      banner.hidden = false;
      banner.innerHTML = `<p><strong>Cancellation status:</strong> ${escapeHtml(info.status)}</p>`;
    }
  }

  async function loadReturn(o, managed) {
    const { ok, data } = await call('/returns');
    if (!ok || !data?.returns) return;
    const info = data.returns[orderId];
    if (!info) return;
    // The return buttons live inside loadManage()-rendered blocks.
    await managed;
    const banner = root.querySelector('[data-return-status]');
    if (!banner) return;
    // Headline only. The per-location rows this used to render ("Shop location:
    // Return label ready — Download return label ↗ (FedEx …)") named a warehouse
    // instead of the item and repeated on every visit; the label now lives on
    // the shipment card holding the items it covers.
    const headline = RETURN_STATUS_LABELS[info.status] || 'Return in progress';
    banner.hidden = false;
    banner.innerHTML = `<p><strong>${escapeHtml(headline)}.</strong> Details are on the shipment below.</p>`;
    // Deliberately no button-hiding here. This used to blank EVERY "Request
    // return" button on the order the moment any return existed, because the
    // server held one pending return per ORDER and 409'd the second. Both are
    // gone: returns are per-destination now, and the button set is already
    // correct without us pruning it — `returnableGroups` comes from Shopify's
    // returnableFulfillments, which drops quantities that are under an open
    // return. An item that can't be returned never gets a button in the first
    // place, and one that can keeps it while a different parcel is in flight.
  }

  // ── Action click handler ─────────────────────────────────────────────────

  function wireActions(order) {
    root.addEventListener('click', async (e) => {
      const btn = e.target.closest('button[data-odp-action]');
      if (!btn) return;

      if (btn.dataset.odpAction === 'cancel') {
        const panel = panelFor(btn);
        if (!panel) return;
        panel.hidden = false;
        const isPartial = btn.dataset.partial === 'true';
        const partialNotice = isPartial
          ? `<div class="cancel-form__partial-notice" role="note"><p><strong>Some items in this order have already shipped.</strong> Only the items that haven't shipped yet can be cancelled — already-shipped items would need to be returned instead. Our team will confirm what can be cancelled.</p></div>`
          : '';
        const reasonHtml = CANCEL_REASONS.map((r, i) => {
          const value = typeof r === 'string' ? r : r.value;
          const label = typeof r === 'string' ? r : r.label;
          const hint = typeof r === 'string' ? '' : r.hint;
          const isDefault = value === DEFAULT_CANCEL_REASON;
          return `
            <label class="cancel-form__reason">
              <input type="radio" name="reason" value="${escapeHtml(value)}" ${i === 0 ? 'required' : ''} ${isDefault ? 'checked' : ''} />
              <span>${escapeHtml(label)}${hint ? `<span class="cancel-form__reason-hint">${escapeHtml(hint)}</span>` : ''}</span>
            </label>`;
        }).join('');
        panel.innerHTML = `
          <form data-cancel-form>
            <p class="order-card__panel-title">Why are you cancelling?</p>
            ${partialNotice}
            <div class="cancel-form__reasons">
              ${reasonHtml}
              <label class="cancel-form__reason">
                <input type="radio" name="reason" value="__other__" />
                <span>Other (please specify)</span>
              </label>
            </div>
            <textarea name="other_text" class="cancel-form__note" placeholder="Tell us more (optional)…"></textarea>
            <div class="cancel-form__actions">
              <button type="button" class="btn-outline" data-odp-action="cancel-dismiss">Nevermind</button>
              <button type="submit" class="btn-primary">Submit request</button>
            </div>
            <small class="cancel-form__footnote">Our team will review and email you with a decision.</small>
          </form>`;
        const form = panel.querySelector('[data-cancel-form]');
        const other = form.querySelector('textarea[name="other_text"]');
        form.addEventListener('change', (ev) => {
          if (ev.target.name === 'reason') {
            other.required = ev.target.value === '__other__';
            other.placeholder = ev.target.value === '__other__' ? 'Please describe your reason…' : 'Tell us more (optional)…';
          }
        });
        form.addEventListener('submit', async (ev) => {
          ev.preventDefault();
          const choice = form.reason.value;
          const reason = choice === '__other__' ? other.value.trim() : choice;
          if (!reason) return;
          const submit = form.querySelector('button[type="submit"]');
          submit.disabled = true;
          setFormError(form, '');
          const { ok, data } = await call(`/orders/${orderId}/cancel`, { method: 'POST', body: { reason } });
          if (!ok) {
            if (data?.error === 'cancel_window_closed') {
              // Closed between page load and submit (an hour-scale window can
              // do that), or the page was open before the rule changed.
              const latest = (data.lines || []).reduce((m, l) => (l.deadlineAt && (!m || l.deadlineAt > m) ? l.deadlineAt : m), null);
              panel.innerHTML = `
                <p class="order-card__panel-title">Cancellation window closed</p>
                <p>${latest ? `The window to cancel ${data.lines.length === 1 ? `<em>${escapeHtml(data.lines[0].title || 'this item')}</em>` : 'these items'} online closed on ${escapeHtml(formatDeadline(latest, true))}.` : "These items can't be cancelled online."} Once they arrive you can request a return, or contact Birch support if something is wrong.</p>`;
              const closedBtn = root.querySelector('button[data-odp-action="cancel"]');
              if (closedBtn) closedBtn.hidden = true;
              return;
            }
            if (data?.error === 'use_returns') {
              // A return needs something delivered to send back. Offer the
              // button only when a shipment can actually take one; an order
              // still with the supplier gets the plain next step instead of a
              // button that has nothing to open (it used to do nothing).
              // No "Request a return instead" button (owner, 2026-10-08): the
              // panel says what happens next, and a delivered item's own
              // "Request return" button sits on its shipment.
              const canReturn = !!firstLiveReturnButton();
              panel.innerHTML = `
                <p class="order-card__panel-title">Cancellation not available</p>
                ${canReturn
                  ? `<p>The items on this order have already been sent to the supplier or shipped, so they can no longer be cancelled. Delivered items can be returned with <strong>Request return</strong> on their shipment.</p>`
                  : `<p>Your order has already gone to the supplier to be prepared, so it can no longer be cancelled. Once it's delivered, you can request a return right here on this page.</p>`}
                <p><small>Something wrong with the order? Contact Birch support and we'll help.</small></p>`;
              // Cancelling was just refused — the button and its "Cancel by"
              // deadline would only invite a second try.
              root.querySelectorAll('.odp__cancel-action').forEach(el => { el.hidden = true; });
              return;
            }
            setFormError(form, friendlyError(data?.error, 'Could not submit request — please try again.'));
            submit.disabled = false;
            return;
          }
          panel.hidden = true;
          panel.innerHTML = '';
          if (data?.status === 'cancelled') {
            const banner = root.querySelector('[data-cancellation]');
            if (banner) { banner.hidden = false; banner.innerHTML = `<p><strong>Order cancelled.</strong> Your refund is being processed.</p>`; }
          }
          loadCancellation(order);
        });
        return;
      }

      if (btn.dataset.odpAction === 'invoice') {
        const panel = root.querySelector('[data-odp-invoice]');
        if (!panel) return;
        const opening = panel.hidden;
        panel.hidden = !opening;
        btn.textContent = opening ? 'Hide invoice' : 'View invoice';
        btn.setAttribute('aria-expanded', String(opening));
        if (opening) panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }

      if (btn.dataset.odpAction === 'invoice-close') {
        const panel = root.querySelector('[data-odp-invoice]');
        const toggle = root.querySelector('button[data-odp-action="invoice"]');
        if (panel) panel.hidden = true;
        if (toggle) {
          toggle.textContent = 'View invoice';
          toggle.setAttribute('aria-expanded', 'false');
          toggle.focus();
        }
        return;
      }

      if (btn.dataset.odpAction === 'invoice-print') {
        const invoice = root.querySelector('[data-odp-invoice] .invoice');
        if (!invoice) return;
        // Move the invoice out to be a direct child of <body> for printing. The
        // rest of the page stays in the document otherwise, and merely hiding it
        // with visibility:hidden keeps its boxes — so the print spans blank
        // pages. Pulling the invoice out and display:none-ing the rest collapses
        // the print to just the invoice. Anchored back via a placeholder.
        const placeholder = document.createComment('invoice-placeholder');
        invoice.parentNode.insertBefore(placeholder, invoice);
        document.body.appendChild(invoice);
        document.body.classList.add('printing-invoice');
        invoice.classList.add('invoice--printing');

        let restored = false;
        const restore = () => {
          if (restored) return;
          restored = true;
          document.body.classList.remove('printing-invoice');
          invoice.classList.remove('invoice--printing');
          if (placeholder.parentNode) placeholder.parentNode.replaceChild(invoice, placeholder);
          window.removeEventListener('afterprint', restore);
        };
        // afterprint covers browsers where print() returns before the dialog
        // closes; the timeout is a fallback for those that never fire it.
        window.addEventListener('afterprint', restore);
        window.print();
        setTimeout(restore, 1000);
        return;
      }

      if (btn.dataset.odpAction === 'label-preview') {
        const box = document.getElementById(btn.dataset.labelTarget);
        if (!box) return;
        const opening = box.hidden;
        box.hidden = !opening;
        btn.setAttribute('aria-expanded', String(opening));
        btn.textContent = opening ? 'Hide preview' : 'Preview';
        // Embed lazily: the label is a signed PDF URL, and fetching every one on
        // page load would be wasteful for a customer who only wants to download.
        if (opening && !box.dataset.loaded) {
          box.dataset.loaded = '1';
          box.innerHTML = `
            <iframe class="odp__label-frame" src="${escapeHtml(box.dataset.labelSrc)}#toolbar=0&view=FitH"
                    title="Return shipping label" loading="lazy"></iframe>
            <p class="odp__label-fallback"><small>Can't see it? <a href="${escapeHtml(box.dataset.labelSrc)}" target="_blank" rel="noopener noreferrer">Open the label in a new tab</a>.</small></p>`;
        }
        return;
      }

      if (btn.dataset.odpAction === 'cancel-dismiss') {
        const panel = panelFor(btn);
        if (panel) { panel.hidden = true; panel.innerHTML = ''; }
        return;
      }

      if (btn.dataset.odpAction === 'make-it-right') {
        const gi = Number(btn.dataset.groupIndex);
        const g = returnableGroups[gi];
        const panel = panelFor(btn);
        if (!panel || !g) return;
        const only = btn.dataset.itemIndex !== undefined ? Number(btn.dataset.itemIndex) : null;
        // The per-item link sits inside the open return form, and this panel
        // swap replaces that form — ask before throwing away answers the
        // customer already gave for the other items.
        const openReturn = panel.querySelector('[data-return-form]');
        if (openReturn && openReturn.dataset.dirty
          && !window.confirm('Switching to "make it right" clears the answers you gave in this return form. Continue?')) return;
        panel.hidden = false;
        panel.innerHTML = renderMakeItRightForm(g, gi, only);
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        const form = panel.querySelector('[data-mir-form]');
        form.addEventListener('change', () => {
          form.querySelector('[data-mir-safety]').hidden = form.mir_reason.value !== 'SAFETY';
        });
        form.addEventListener('submit', async (ev) => {
          ev.preventDefault();
          setFormError(form, '');
          const picked = g.items.map((it, ii) => ({ it, ii })).filter(({ ii }) => form[`mir_item_${ii}`] && form[`mir_item_${ii}`].checked);
          if (!picked.length) return setFormError(form, 'Select the item that needs help.');
          const reason = form.mir_reason.value;
          if (!reason) return setFormError(form, "Tell us what the problem is.");
          const note = form.mir_note.value.trim();
          if (!note) return setFormError(form, "Tell our team what's wrong so they can make it right.");
          for (const { it, ii } of picked) {
            const q = Number(form[`mir_qty_${ii}`].value);
            if (!Number.isInteger(q) || q < 1 || q > it.quantity) return setFormError(form, 'Enter a valid quantity for each selected item.');
          }
          const submit = form.querySelector('button[type="submit"]');
          submit.disabled = true;
          let res;
          try {
            res = await requestMakeItRight(picked.map(({ it, ii }) => ({
              lineItemId: it.lineItemId, title: it.title, quantity: Number(form[`mir_qty_${ii}`].value), reason,
            })), note);
          } catch {
            res = { ok: false, html: '' };
          }
          if (!res.ok) {
            // One failure message at a time, and the button back for a retry.
            panel.querySelectorAll('[data-mir-fail]').forEach(el => el.remove());
            const failHtml = res.html || `<p class="return-summary return-summary--fail">We couldn't send your request. Please email Birch support, or try again in a moment.</p>`;
            panel.insertAdjacentHTML('beforeend', `<div data-mir-fail>${failHtml}</div>`);
            submit.disabled = false;
            return;
          }
          panel.innerHTML = res.html;
          await refreshReturnActions(order).catch(() => {});
        });
        return;
      }

      if (btn.dataset.odpAction === 'return') {
        // A return is now scoped to ONE shipment. The cancellation-denied banner
        // still fires this without an index, so fall back to the first group.
        // The cancellation-denied banner fires this from outside any card, so
        // hand off to the first real return button rather than guessing.
        if (btn.dataset.groupIndex === undefined) {
          const real = firstLiveReturnButton();
          if (real) { real.click(); return; }
          // Nothing returnable yet (not delivered, already returned, or past
          // the window): say so where the click happened rather than nothing.
          const host = btn.closest('[data-shipment-panel], [data-cancellation], .odp__cancellation-cta') || btn.parentElement;
          if (host && !host.querySelector('[data-return-unavailable]')) {
            host.insertAdjacentHTML('beforeend', '<p class="odp__window-closed" data-return-unavailable>Nothing on this order can be returned yet — a return opens once your items are delivered.</p>');
          }
          btn.hidden = true;
          return;
        }
        const gi = Number(btn.dataset.groupIndex);
        const g = returnableGroups[gi];
        const panel = panelFor(btn);
        if (!panel || !g) return;
        // Identifies THIS submission to the server's per-group idempotency
        // claim. Minted once per form open, so a double-click or a retry of the
        // same form is recognised as one request, while a return the customer
        // starts later — even for the same items and quantity, which a partial
        // return makes possible — is correctly treated as a new one.
        const submissionToken = (self.crypto && self.crypto.randomUUID)
          ? self.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        panel.hidden = false;
        panel.innerHTML = `
          <form data-return-form>
            ${returnableGroups.length > 1 ? '<p><small>This request covers the items in this shipment only — the others are returned separately.</small></p>' : ''}
            ${renderReturnFieldset(g, gi)}
            <div class="cancel-form__actions">
              <button type="button" class="btn-outline" data-odp-action="return-dismiss" data-group-index="${gi}">Nevermind</button>
              <button type="submit" class="btn-primary">Submit return request</button>
            </div>
          </form>`;
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        const returnForm = panel.querySelector('[data-return-form]');
        returnForm.addEventListener('change', () => syncReturnSteps(returnForm, g, gi));
        // Any answer given — the make-it-right link confirms before replacing it.
        const markDirty = () => { returnForm.dataset.dirty = '1'; };
        returnForm.addEventListener('change', markDirty);
        returnForm.addEventListener('input', markDirty);
        wireReturnSteps(returnForm, g, gi);
        syncReturnSteps(returnForm, g, gi);
        returnForm.addEventListener('submit', async (ev) => {
          ev.preventDefault();
          const form = ev.currentTarget;
          setFormError(form, '');
          // Only this shipment's group is in the form, so only it is submitted.
          const note = form[`note_${gi}`].value;
          const refundMethod = g.containsRegistryGift
            ? 'store_credit'
            : (form[`refund_method_${gi}`]?.value || DEFAULT_REFUND_METHOD);
          const a = itemAnswers(form, g, gi);
          if (!a.items.length) return setFormError(form, 'Select at least one item to return.');
          for (const x of a.items) {
            if (!Number.isInteger(x.qty) || x.qty < 1 || x.qty > x.it.quantity) return setFormError(form, 'Enter a valid quantity (at least 1, no more than the amount ordered) for each selected item.');
            const who = a.items.length > 1 ? ` for ${x.it.title}` : '';
            if (!x.reason) return setFormError(form, `Tell us why you are returning it${who}.`);
            if (!x.condition && x.resolution !== 'support') return setFormError(form, `Tell us what condition it's in${who}.`);
            if (!x.resolution) return setFormError(form, `Choose what you'd like${who}.`);
          }
          const notAccepted = a.items.filter(x => x.resolution !== 'support' && x.condition !== 'n/a' && conditionRestricted(x.it) && !x.it.policy.conditions.includes(x.condition));
          if (notAccepted.length) {
            return setFormError(form, notAccepted.map(x => `${x.it.title} can only be returned if ${conditionsPhrase(x.it.policy.conditions)}.`).join(' ')
              + ' Untick it to return the rest, or contact Birch support.');
          }
          const supporting = a.items.filter(x => x.resolution === 'support');
          if (supporting.length && !String(note || '').trim()) {
            return setFormError(form, "Tell our team what's wrong so they can make it right.");
          }
          const keeping = a.items.filter(x => x.resolution === 'keep');
          const returning = a.items.filter(x => x.resolution !== 'keep' && x.resolution !== 'support');
          const submit = form.querySelector('button[type="submit"]');
          submit.disabled = true;

          // The return first: it's the request most likely to be refused (a
          // window or condition rule), and nothing should be credited when the
          // customer has to come back and change their answers anyway.
          let resp = null;
          if (returning.length) {
            const groups = [{
              locationId: g.locationId, locationName: g.locationName, refundMethod, submissionToken,
              items: returning.map(x => ({
                fulfillmentLineItemId: x.it.fulfillmentLineItemId, lineItemId: x.it.lineItemId, quantity: x.qty,
                // With items going to the team, the note is their description of
                // THOSE items — don't stamp it on the ones being returned.
                returnReason: x.reason, customerNote: (!supporting.length && note) || undefined,
                ...(x.condition !== 'n/a' ? { condition: x.condition } : {}),
                ...(x.offers ? { resolution: x.resolution } : {}),
                ...(x.resolution === 'exchange' ? { exchangeVariantId: (form[`exchange_${gi}_${x.ii}`] || {}).value } : {}),
              })),
            }];
            const res = await call(`/orders/${orderId}/returns`, { method: 'POST', body: { groups } });
            resp = res.data;
            if (!resp || (!res.ok && !resp.created?.length)) {
              let msg = friendlyError(resp?.error, "We couldn't submit your return — please try again.");
              if (resp?.error === 'return_window_closed' && Array.isArray(resp.lines) && resp.lines.length) {
                msg = resp.lines.map(l => l.reason === 'not_allowed'
                  ? `${l.title || 'An item'} is final sale and can't be returned.`
                  : `The return window for ${l.title || 'an item'} closed${l.deadlineAt ? ` on ${formatDeadline(l.deadlineAt, false)}` : ''}.`).join(' ')
                  + ' Untick it to return the rest, or contact Birch support.';
              }
              if (resp?.error === 'condition_not_accepted' && Array.isArray(resp.lines) && resp.lines.length) {
                msg = resp.lines.map(l => `${l.title || 'An item'} can only be returned if ${conditionsPhrase(l.accepted)}.`).join(' ')
                  + ' Untick it to return the rest, or contact Birch support.';
              }
              if (resp?.error === 'kept_with_credit' && Array.isArray(resp.lines) && resp.lines.length) {
                msg = `You kept ${resp.lines.map(l => l.title || 'an item').join(', ')} with a Birch Credit, so ${resp.lines.length === 1 ? 'it' : 'they'} can't be returned as well.`;
              }
              if (resp?.error === 'exchange_not_available' && Array.isArray(resp.lines) && resp.lines.length) {
                msg = `An exchange isn't available for ${resp.lines.map(l => l.title || 'an item').join(', ')} any more — it may have just sold out. Choose a refund instead.`;
              }
              setFormError(form, msg);
              submit.disabled = false;
              return;
            }
          }
          // The return (if any) is already created at this point, so a failure
          // in a follow-up must still land on the "submitted" summary below —
          // never a frozen form that hides the return the customer now has.
          const kept = [];
          for (const x of keeping) {
            try {
              kept.push(await keepItem(x, note));
            } catch {
              kept.push({ ok: false, title: x.it.title, message: `We couldn't add the credit for ${x.it.title} — nothing was credited. You can request a return for it instead.` });
            }
          }
          let supported = null;
          if (supporting.length) {
            const mirItems = supporting.map(x => ({ lineItemId: x.it.lineItemId, title: x.it.title, quantity: x.qty, reason: x.reason }));
            try {
              supported = await requestMakeItRight(mirItems, note);
            } catch {
              supported = { ok: false, html: `<p class="return-summary return-summary--fail">We couldn't send your request for ${escapeHtml(mirItems.map(x => x.title).join(', '))}. Please email Birch support, or try again in a moment.</p>` };
            }
          }

          const exchanged = returning.filter(x => x.resolution === 'exchange');
          const refunded = returning.filter(x => x.resolution === 'refund');
          const fails = ((resp && resp.failures) || []).map(f => {
            const errs = Array.isArray(f.errors) ? f.errors : [];
            const why = errs.map(e => e && e.message).filter(Boolean).join('; ');
            return `${f.locationName}${why ? ` — ${why}` : ''}`;
          });
          const dupe = resp && (resp.duplicates || []).length && !(resp.created || []).length;
          const names = (xs) => xs.map(x => x.it.title + (x.qty > 1 ? ` × ${x.qty}` : '')).join(', ');
          const lines = [];
          if (returning.length && !dupe) {
            lines.push(`<p class="order-card__panel-title">${exchanged.length && !refunded.length ? 'Exchange request submitted' : 'Return request submitted'}</p>`);
            lines.push(`<p>You'll get an email with return instructions for ${escapeHtml(names(returning))}.</p>`);
            if (refunded.length) {
              lines.push(`<p><small>Refund for ${escapeHtml(names(refunded))} to: ${escapeHtml(refundMethod === 'store_credit'
                ? (g.containsRegistryGift ? 'Birch Credit to the registry owner' : 'Birch Credit')
                : (originalPaymentLabel() || 'your original payment method'))}${refundMethod === 'store_credit' ? '' : ' — once we receive the item'}</small></p>`);
            }
            if (exchanged.length) {
              lines.push(`<p><small>Replacement for ${escapeHtml(names(exchanged))} ships once we receive ${exchanged.length === 1 ? 'it' : 'them'}.</small></p>`);
            }
          }
          if (dupe) lines.push(`<p class="order-card__panel-title">Already requested</p><p>A return for these items is already in progress.</p>`);
          if (supported) lines.push(supported.html);
          kept.filter(k => k.ok).forEach(k => lines.push(`<p><strong>Birch Credit added:</strong> ${escapeHtml(formatMoney(k.amount, k.currency))} for ${escapeHtml(k.title)} — keep it, there's nothing to send back.</p>`));
          kept.filter(k => !k.ok).forEach(k => lines.push(`<p class="return-summary return-summary--fail">${escapeHtml(k.message)}</p>`));
          if (fails.length) lines.push(`<p class="return-summary return-summary--fail">Not accepted: ${escapeHtml(fails.join('; '))}. If this looks wrong, contact Birch support — don't resubmit.</p>`);
          panel.innerHTML = lines.join('');
          if (returning.length) shipmentIdsFor(order, g).forEach(id => submittedShipments.add(id));
          // Whatever is still returnable (other items, the rest of a quantity)
          // gets its button back straight away — "Return other items" — instead
          // of only after a reload.
          await refreshReturnActions(order).catch(() => {});
          loadReturn(order).catch(() => {});
        });
      }

      if (btn.dataset.odpAction === 'return-dismiss') {
        const panel = panelFor(btn);
        if (panel) { panel.hidden = true; panel.innerHTML = ''; }
        return;
      }
    });
  }

  // ── Tracking helpers ─────────────────────────────────────────────────────

  // Carrier/fulfillment status codes are machine strings ("IN_TRANSIT",
  // "LABEL_PRINTED") — the page used to print them raw. Map to plain language.
  const SHIPMENT_STATES = {
    DELIVERED: { label: 'Delivered', tone: 'done' },
    OUT_FOR_DELIVERY: { label: 'Out for delivery', tone: 'transit' },
    IN_TRANSIT: { label: 'In transit', tone: 'transit' },
    ATTEMPTED_DELIVERY: { label: 'Delivery attempted', tone: 'alert' },
    READY_FOR_PICKUP: { label: 'Ready for pickup', tone: 'transit' },
    PICKED_UP: { label: 'Picked up', tone: 'transit' },
    CONFIRMED: { label: 'Shipment confirmed', tone: 'pending' },
    LABEL_PRINTED: { label: 'Label created', tone: 'pending' },
    LABEL_PURCHASED: { label: 'Label created', tone: 'pending' },
    LABEL_VOIDED: { label: 'Label voided', tone: 'alert' },
    INFO_RECEIVED: { label: 'Label created', tone: 'pending' },
    FULFILLED: { label: 'Shipped', tone: 'transit' },
    SUCCESS: { label: 'Shipped', tone: 'transit' },
    FAILURE: { label: 'Delivery issue', tone: 'alert' },
    ERROR: { label: 'Delivery issue', tone: 'alert' },
    EXPIRED: { label: 'Tracking expired', tone: 'alert' },
    PENDING: { label: 'Preparing to ship', tone: 'pending' },
  };

  // ── Shared utilities ─────────────────────────────────────────────────────

  // Never throws: a network failure (offline, DNS, aborted) resolves to
  // { ok: false, status: 0, data: null } so every submit handler can re-enable
  // its button and say what happened instead of hanging.
  async function call(path, opts = {}) {
    const route = path.charAt(0) === '/' ? path : '/' + path;
    let res;
    try {
      res = await fetch(PROXY + route, {
        method: opts.method || 'GET',
        headers: opts.body ? { 'Content-Type': 'application/json' } : {},
        body: opts.body ? JSON.stringify(opts.body) : undefined,
        credentials: 'same-origin',
      });
    } catch {
      return { ok: false, status: 0, data: null };
    }
    return { ok: res.ok, status: res.status, data: await res.json().catch(() => null) };
  }

  function formatMoney(amount, currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(parseFloat(amount));
    } catch {
      return `${currencyCode} ${parseFloat(amount).toFixed(2)}`;
    }
  }

  function formatDate(isoString, opts) {
    try {
      return new Intl.DateTimeFormat(undefined, opts).format(new Date(isoString));
    } catch {
      return isoString ? isoString.slice(0, 10) : '';
    }
  }

  function titleCase(str) {
    return String(str).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }

  function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // Escape first, then honour the line breaks the shopper typed.
  function nl2br(s) {
    return escapeHtml(s).replace(/\n/g, '<br>');
  }

  function statusIcon(type) {
    if (type === 'cancelled') return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
    if (type === 'done') return checkIcon(16);
    return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><polyline points="12 7 12 12 15 14"></polyline></svg>`;
  }

  function checkIcon(size) {
    return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
  }

  function cancelIcon(size) {
    return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
  }

  const ERROR_MESSAGES = {
    already_requested: 'A cancellation request is already pending for this order.',
    already_fulfilled: 'This order has already shipped and can no longer be cancelled.',
    // Scoped to the shipment, not the order: other parcels stay returnable, so
    // an order-wide "you already have a return" would read as a dead end.
    return_already_pending: "A return for these items is already in progress — we'll email you once it's processed. Your other shipments can still be returned.",
    return_window_closed: 'The return window for these items has closed.',
    cancel_window_closed: 'The window to cancel these items online has closed.',
    group_gift_contribution_not_returnable: "Group-gift contributions can't be returned individually.",
    no_items: 'Select at least one item to return.',
    condition_not_accepted: "These items can't be returned in that condition.",
    exchange_not_available: "An exchange isn't available for these items any more — choose a refund instead.",
    bad_condition: 'Tell us what condition the item is in.',
    kept_with_credit: "Items you kept with a Birch Credit can't be returned as well.",
  };
  function friendlyError(code, fallback) { return (code && ERROR_MESSAGES[code]) || fallback; }

  function setFormError(form, msg) {
    let el = form.querySelector('[data-form-error]');
    if (!msg) { if (el) el.remove(); return; }
    if (!el) {
      el = document.createElement('p');
      el.className = 'odp__form-error';
      el.setAttribute('role', 'alert');
      el.setAttribute('data-form-error', '');
      form.appendChild(el);
    }
    el.textContent = msg;
  }
})();
